/**
 * Service Firestore pour ATABLE
 * Remplace api.ts pour toutes les opérations CRUD via Firebase
 */

import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    serverTimestamp,
    Timestamp,
    setDoc,
    runTransaction,
    deleteField,
} from 'firebase/firestore';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    getAuth,
    type User as FirebaseUser,
} from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db, loadStorage } from './firebase';
import app from './firebase';
import { callableErrorReason, checkinMessage } from '../checkinErrors';
import { visitAmountCents } from '../visitAmount';
import type { AnalyticsRaw } from '../analytics/types';
import type {
    User,
    Restaurant,
    SegmentRules,
    SegmentRulesDoc,
    CustomerSegmentKey,
    CustomerSegmentStats,
    Campaign,
    FlashPromotion,
    Reservation,
    MenuItem,
    POSOrder,
    Customer,
    Reward,
    RestaurantPhoto,
} from '../types';
import { todayISO, getFlashPromoStatus, flashCampaignDraft, flashOfferQuantity, restoredFlashStock } from '../flashPromo';

const FLASH_PROMO_ENDED = 'Cette offre est terminée.';
import { SEGMENT_KEYS } from '../customerSegmentation';
import { MAX_DISH_PHOTOS, safeStorageImageUrl } from '../restaurantShowcase';
import {
    MAX_MENU_CHANGES_PER_WRITE,
    MENU_ITEM_ID_MISSING,
    changedMenuItems,
    isMenuItemId,
    mergeMenuForPublish,
    menuOf,
    menuPhotoPath,
    removeMenuItem,
    repairMenuItemIds,
    setMenuItemPhoto,
    unusedMenuPhotoPaths,
    upsertMenuItem,
} from '../menuPhotos';
import { UserFacingError, isChunkLoadError } from '../userFacingError';
// Demandes d'accès restaurateur (section « DEMANDES D'ACCÈS RESTAURATEUR »)
import { writeBatch, limit } from 'firebase/firestore';
import { sendEmailVerification, reload } from 'firebase/auth';
import {
    REJECTED_APPLICATION_RETENTION_DAYS,
    RESTAURATEUR_TERMS_VERSION,
    validateRejectionReason,
    type RestaurateurApplication,
    type RestaurateurApplicationInput,
    type RestaurateurSignupField,
} from '../restaurateurApplication';

// ============================================
// HELPERS
// ============================================

const toDate = (ts: any): string => {
    if (!ts) return new Date().toISOString();
    if (ts instanceof Timestamp) return ts.toDate().toISOString();
    if (typeof ts === 'string') return ts;
    return new Date().toISOString();
};

// ============================================
// FIRESTORE SERVICE CLASS
// ============================================

/** Puce NFC du restaurant, telle que la renvoie getNfcTokens. */
export interface NfcTagInfo {
    nonce: string;
    label: string;
    /** URL à programmer sur la puce (NDEF « URI »). */
    url: string;
    createdAt: number | null;
    batchId: string;
    /** Désactivée par le restaurant. */
    consumed: boolean;
    lastUsedAt: number | null;
    usageCount: number;
}

/** Résultat de l'encaissement d'une commande de caisse (Cloud Function payPosOrder). */
export interface PosPaymentResult {
    success: true;
    paid: true;
    /** Fidélité créditée au membre lié à la commande. */
    credited: boolean;
    /**
     * Sans crédit : no_customer | not_member | client_suspended | restaurant_inactive | no_amount
     * | amount_too_high | already_validated_today | no_credit_without_amount | already_paid.
     */
    reason?: string;
    clientName?: string;
    /** Montant crédité (centimes, plafonné à la valeur des plats de la carte). */
    amountCents?: number;
    pointsEarned?: number;
    stampsEarned?: number;
    programType?: string;
}

class FirestoreService {
    private listeners: Function[] = [];
    private currentUser: User | null = null;
    private authUnsubscribe: (() => void) | null = null;
    private userDocUnsubscribe: (() => void) | null = null;
    private authReady: boolean = false;

    // Caches temps réel — mis à jour via onSnapshot
    private _usersCache: User[] = [];
    private _restaurantsCache: Restaurant[] = [];
    private _usersSnapUnsub: (() => void) | null = null;
    private _restaurantsSnapUnsub: (() => void) | null = null;

    constructor() {
        // Écouter les changements d'état d'authentification
        this.authUnsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            // Annuler l'écoute de l'ancien profil
            if (this.userDocUnsubscribe) {
                this.userDocUnsubscribe();
                this.userDocUnsubscribe = null;
            }

            if (firebaseUser) {
                // Écoute TEMPS RÉEL du profil Firestore
                const userRef = doc(db, 'users', firebaseUser.uid);
                this.userDocUnsubscribe = onSnapshot(userRef, async (snap) => {
                    // Inscription / connexion en cours : la méthode en cours fixe elle-même
                    // l'utilisateur une fois le serveur d'accord (cf. _authFlowInFlight).
                    if (this._authFlowInFlight) return;
                    if (!snap.exists()) {
                        // Profil supprimé de Firestore → déconnexion
                        console.warn('[Auth] Profil utilisateur supprimé de Firestore — déconnexion');
                        this.currentUser = null;
                        await signOut(auth);
                    } else {
                        const userData = { id: firebaseUser.uid, ...snap.data() } as User;
                        if (userData.status === 'SUSPENDED') {
                            // Compte suspendu → déconnexion immédiate
                            console.warn('[Auth] Compte suspendu — déconnexion forcée');
                            this.currentUser = null;
                            localStorage.removeItem('user');
                            await signOut(auth);
                        } else {
                            this.currentUser = userData;
                        }
                    }
                    this._syncAdminListeners(this.currentUser);
                    this.authReady = true;
                    this.notifyListeners();
                }, (error) => {
                    console.error('[Auth] Erreur écoute profil:', error);
                    this.authReady = true;
                    this.notifyListeners();
                });
            } else {
                this.currentUser = null;
                this._syncAdminListeners(null);
                this.authReady = true;
                this.notifyListeners();
            }
        });

    }

    /**
     * Écoute temps réel de TOUS les profils et restaurants : réservée à l'admin (panneau
     * admin). Les règles refusent de lister `users` à tout autre compte (RGPD) ; le
     * restaurateur ne lit que les profils de ses membres, un par un.
     */
    private _syncAdminListeners(user: User | null) {
        const isAdmin = !!user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN');
        if (isAdmin && !this._usersSnapUnsub) {
            this._startCollectionListeners();
        } else if (!isAdmin && this._usersSnapUnsub) {
            this._usersSnapUnsub();
            this._restaurantsSnapUnsub?.();
            this._usersSnapUnsub = null;
            this._restaurantsSnapUnsub = null;
            this._usersCache = [];
            this._restaurantsCache = [];
        }
    }

    /**
     * onSnapshot sur collections users + restaurants (admin uniquement, cf. _syncAdminListeners).
     * Tout changement (console Firebase, backend, autre client) se propage automatiquement.
     */
    private _startCollectionListeners() {
        this._usersSnapUnsub = onSnapshot(
            collection(db, 'users'),
            (snap) => {
                this._usersCache = snap.docs.map((d) => ({ id: d.id, ...d.data() } as User));
                this.notifyListeners();
            },
            (err) => console.error('[onSnapshot users]', err)
        );

        this._restaurantsSnapUnsub = onSnapshot(
            collection(db, 'restaurants'),
            (snap) => {
                this._restaurantsCache = snap.docs.map((d) => ({ id: d.id, ...d.data() } as any));
                this.notifyListeners();
            },
            (err) => console.error('[onSnapshot restaurants]', err)
        );
    }

    isAuthReady(): boolean {
        return this.authReady;
    }

    subscribe(listener: Function) {
        this.listeners.push(listener);
        // Ne pas appeler listener() immédiatement — attendre que Firebase soit prêt
        // Sauf si l'auth est déjà résolue (ex: re-subscribe après reconnexion)
        if (this.authReady) {
            listener();
        }
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    private notifyListeners() {
        this.listeners.forEach((listener) => listener());
    }

    // ============================================
    // AUTH
    // ============================================

    /**
     * Inscription ou connexion en cours : l'écoute du profil (constructeur) ignore
     * ses événements, sinon l'écriture locale du profil — pas encore confirmée par
     * le serveur — ferait basculer l'interface (formulaire démonté) avant de savoir
     * si l'écriture est acceptée, et un profil encore absent provoquerait une
     * déconnexion. La méthode en cours fixe elle-même l'utilisateur courant.
     */
    private _authFlowInFlight = false;

    /** Délai au-delà duquel une écriture non confirmée (hors ligne) est abandonnée. */
    private static readonly SIGNUP_WRITE_TIMEOUT_MS = 20_000;

    /** Rejette (code « deadline-exceeded ») si la promesse n'aboutit pas à temps. */
    private _withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(
                () => reject(Object.assign(new Error('Délai dépassé'), { code: 'deadline-exceeded' })),
                ms,
            );
            promise.then(
                (value) => { clearTimeout(timer); resolve(value); },
                (error) => { clearTimeout(timer); reject(error); },
            );
        });
    }

    /**
     * Écriture du profil refusée ou non confirmée après une création de compte :
     * on supprime le compte Auth pour que la personne puisse recommencer avec la
     * même adresse ; si c'est impossible (hors ligne), simple déconnexion — à la
     * prochaine connexion, login() recrée le profil CLIENT.
     */
    private async _abortSignup(user: FirebaseUser): Promise<'deleted' | 'kept'> {
        try {
            await this._withTimeout(user.delete(), 10_000);
            return 'deleted';
        } catch (error) {
            console.error('[Inscription] suppression du compte inachevé impossible', error);
            await signOut(auth).catch(() => undefined);
            return 'kept';
        }
    }

    async login(email: string, password: string): Promise<User> {
        this._authFlowInFlight = true;
        try {
            const credential = await signInWithEmailAndPassword(auth, email, password);
            const userRef = doc(db, 'users', credential.user.uid);
            const userDoc = await getDoc(userRef);
            let userData: User;
            if (!userDoc.exists()) {
                // Compte Auth sans profil (inscription interrompue) : profil CLIENT
                // recréé, comme à l'inscription — les règles n'autorisent rien d'autre.
                const profile: Omit<User, 'id'> = {
                    email: credential.user.email ?? email,
                    firstName: '',
                    lastName: '',
                    role: 'CLIENT',
                    status: 'ACTIVE',
                    createdAt: new Date().toISOString(),
                };
                try {
                    await this._withTimeout(setDoc(userRef, profile), FirestoreService.SIGNUP_WRITE_TIMEOUT_MS);
                } catch (error) {
                    console.error('[Auth] recréation du profil', error);
                    await signOut(auth);
                    throw new Error('Connexion impossible pour le moment. Vérifiez votre connexion internet puis réessayez.');
                }
                userData = { id: credential.user.uid, ...profile };
            } else {
                userData = { id: credential.user.uid, ...userDoc.data() } as User;
            }
            if (userData.status === 'SUSPENDED') {
                await signOut(auth);
                throw new Error('Ce compte a été suspendu. Contactez l\'administrateur.');
            }
            this.currentUser = userData;
            this._syncAdminListeners(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            this.notifyListeners();
            return userData;
        } catch (error: any) {
            const msg = error.code === 'auth/invalid-credential'
                ? 'Email ou mot de passe incorrect'
                : error.message || 'Erreur de connexion';
            throw new Error(msg);
        } finally {
            this._authFlowInFlight = false;
        }
    }

    async registerClient(
        email: string,
        password: string,
        firstName: string,
        lastName: string,
    ): Promise<User> {
        this._authFlowInFlight = true;
        try {
            const credential = await createUserWithEmailAndPassword(auth, email, password);
            const userData: Omit<User, 'id'> = {
                email,
                firstName,
                lastName,
                role: 'CLIENT',
                status: 'ACTIVE',
                createdAt: new Date().toISOString(),
            };
            try {
                await this._withTimeout(
                    setDoc(doc(db, 'users', credential.user.uid), userData),
                    FirestoreService.SIGNUP_WRITE_TIMEOUT_MS,
                );
            } catch (writeError) {
                console.error('[Inscription] écriture du profil', writeError);
                const outcome = await this._abortSignup(credential.user);
                throw new Error(outcome === 'deleted'
                    ? "Votre compte n'a pas pu être créé. Vérifiez votre connexion internet puis réessayez."
                    : 'Votre compte a été créé mais son profil n\'a pas pu être enregistré. Connectez-vous dès que votre connexion est rétablie.');
            }
            const user = { id: credential.user.uid, ...userData };
            this.currentUser = user;
            localStorage.setItem('user', JSON.stringify(user));
            this.notifyListeners();
            return user;
        } catch (error: any) {
            const msg = error.code === 'auth/email-already-in-use'
                ? 'Cet email est déjà utilisé'
                : error.message || 'Erreur d\'inscription';
            throw new Error(msg);
        } finally {
            this._authFlowInFlight = false;
        }
    }

    async logout(): Promise<void> {
        this._syncAdminListeners(null);
        await signOut(auth);
        this.currentUser = null;
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        this.notifyListeners();
    }

    getCurrentUser(): User | null {
        if (this.currentUser) return this.currentUser;
        // Fallback localStorage pour compatibilité
        const userStr = localStorage.getItem('user');
        if (!userStr) return null;
        try {
            return JSON.parse(userStr);
        } catch {
            return null;
        }
    }

    // ============================================
    // ADMIN — lecture temps réel (via caches onSnapshot)
    // ============================================

    /**
     * Retourne tous les utilisateurs depuis le cache temps réel.
     * Mis à jour automatiquement dès qu'un changement est fait dans Firestore
     * (console Firebase, backend, admin panel).
     */
    async getUsersAdmin(): Promise<User[]> {
        // Si le cache est vide (premier appel très rapide), on attend un peu
        if (this._usersCache.length === 0) {
            const snap = await getDocs(collection(db, 'users'));
            this._usersCache = snap.docs.map((d) => ({ id: d.id, ...d.data() } as User));
        }
        return this._usersCache;
    }

    async getUserById(id: string): Promise<User | null> {
        try {
            // Cherche d'abord dans le cache temps réel
            const cachedUser = this._usersCache.find(u => u.id === id);
            if (cachedUser) return cachedUser;

            // Sinon fetch depuis Firestore
            const docRef = doc(db, 'users', id);
            const snap = await getDoc(docRef);
            if (!snap.exists()) return null;
            return { id: snap.id, ...snap.data() } as User;
        } catch (error) {
            console.error('Error fetching user:', error);
            return null;
        }
    }

    /**
     * Retourne tous les restaurants (actifs + inactifs) depuis le cache temps réel.
     */
    async getAllRestaurantsAdmin(): Promise<Restaurant[]> {
        if (this._restaurantsCache.length === 0) {
            const snap = await getDocs(collection(db, 'restaurants'));
            this._restaurantsCache = snap.docs.map((d) => ({ id: d.id, ...d.data() } as any));
        }
        return this._restaurantsCache;
    }

    // ============================================
    // RESTAURANTS
    // ============================================

    async getRestaurants(activeOnly: boolean = true): Promise<Restaurant[]> {
        // Utilise le cache temps réel (mis à jour par onSnapshot en continu)
        if (this._restaurantsCache.length > 0) {
            return activeOnly
                ? this._restaurantsCache.filter((r: any) => r.status === 'ACTIVE')
                : this._restaurantsCache;
        }
        // Fallback premier chargement (cache pas encore rempli)
        try {
            const q = activeOnly
                ? query(collection(db, 'restaurants'), where('status', '==', 'ACTIVE'))
                : query(collection(db, 'restaurants'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as any));
        } catch (error) {
            console.error('Error fetching restaurants:', error);
            return [];
        }
    }

    async getRestaurant(id: number | string): Promise<Restaurant | null> {
        try {
            const docRef = doc(db, 'restaurants', String(id));
            const snap = await getDoc(docRef);
            if (!snap.exists()) return null;
            return { id: snap.id, ...snap.data() } as any;
        } catch (error) {
            console.error('Error fetching restaurant:', error);
            return null;
        }
    }

    async updateRestaurantProfile(restaurantId: number | string, data: Partial<Restaurant>): Promise<void> {
        try {
            const payload: Record<string, unknown> = { ...data, updatedAt: serverTimestamp() };
            // Liens facultatifs de la vitrine : vide = « retirer le lien » → champ supprimé
            // (l'app mobile teste la présence du champ, pas une chaîne vide).
            for (const key of ['websiteUrl', 'googleReviewsUrl'] as const) {
                if (key in payload && !payload[key]) payload[key] = deleteField();
            }
            await updateDoc(doc(db, 'restaurants', String(restaurantId)), payload);
            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de mise à jour');
        }
    }

    /**
     * Publication de toute la carte (mode démonstration et API ; en Firebase,
     * l'éditeur enregistre plat par plat et n'appelle plus cette fonction).
     * Non destructive (transaction, cf. mergeMenuForPublish) : reprend les textes
     * des plats existants, ne supprime ni plat ajouté ailleurs ni photo.
     */
    async updateRestaurantMenu(restaurantId: number | string, menu: MenuItem[]): Promise<void> {
        const rid = String(restaurantId);
        await this._writeMenu(
            rid,
            (current) => mergeMenuForPublish(rid, current, menu),
            "La carte n'a pas pu être publiée. Vérifiez votre connexion puis réessayez.",
            `Plus de ${MAX_MENU_CHANGES_PER_WRITE} plats modifiés en une fois : enregistrez-les en plusieurs fois.`,
        );
    }

    // ============================================
    // VITRINE — PHOTOS (Firebase Storage)
    // ============================================
    // Fichiers : restaurants/{restaurantId}/cover/{id}.jpg (photo du lieu) et
    // restaurants/{restaurantId}/dishes/{id}.jpg (galerie de plats), cf. storage.rules.
    // Ordre des opérations : envoi du fichier → écriture Firestore (transaction)
    // → suppression de l'ancien fichier. Si l'écriture échoue, le fichier qui
    // vient d'être envoyé est supprimé ; si une suppression échoue, il reste un
    // fichier orphelin invisible (jamais une image cassée côté client).

    /** Identifiant de fichier aléatoire (crypto.randomUUID n'existe pas en http sur le réseau local). */
    private _newMediaId(): string {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Mot de passe provisoire aléatoire, jamais affiché : le titulaire choisit le sien via l'email
     * de réinitialisation. Le suffixe couvre une éventuelle politique de mot de passe Firebase.
     */
    private _temporaryPassword(): string {
        const bytes = new Uint8Array(24);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('') + 'Aa1!';
    }

    /**
     * Traduit toute erreur en UserFacingError (message français affichable tel
     * quel) ; les erreurs techniques sont journalisées, jamais montrées brutes.
     */
    private _mediaError(error: any, fallback: string): UserFacingError {
        if (error instanceof UserFacingError) return error; // nos propres messages
        const code: string | undefined = error?.code;
        if (code === 'storage/unauthorized' || code === 'permission-denied') {
            return new UserFacingError("Vous n'avez pas les droits pour modifier les photos de ce restaurant.");
        }
        if (code === 'storage/retry-limit-exceeded' || code === 'storage/canceled' || code === 'unavailable'
            || code === 'deadline-exceeded') {
            return new UserFacingError("La connexion est trop lente ou a été interrompue. Vérifiez votre connexion puis réessayez.");
        }
        if (code === 'storage/quota-exceeded') {
            return new UserFacingError("L'espace de stockage des photos est plein. Contactez le support atable.");
        }
        if (isChunkLoadError(error)) {
            return new UserFacingError("L'application a été mise à jour : rechargez la page pour envoyer vos photos.");
        }
        console.error('[Vitrine]', error);
        return new UserFacingError(fallback);
    }

    private async _uploadRestaurantImage(
        restaurantId: string,
        folder: 'cover' | 'dishes' | 'menu',
        image: Blob,
    ): Promise<{ id: string; url: string; storagePath: string }> {
        const { sdk, storage } = await loadStorage();
        const id = this._newMediaId();
        const storagePath = `restaurants/${restaurantId}/${folder}/${id}.jpg`;
        const fileRef = sdk.ref(storage, storagePath);
        await sdk.uploadBytes(fileRef, image, {
            contentType: 'image/jpeg',
            // Cache d'un jour seulement : une photo retirée doit disparaître rapidement.
            cacheControl: 'public, max-age=86400',
        });
        try {
            return { id, url: await sdk.getDownloadURL(fileRef), storagePath };
        } catch (error) {
            await this._deleteRestaurantImage(restaurantId, folder, storagePath);
            throw error;
        }
    }

    /**
     * Suppression best-effort d'un fichier de la vitrine ou de la carte. Le chemin
     * (lu dans le document) doit avoir exactement la forme écrite par
     * _uploadRestaurantImage, dans le dossier attendu (`folder`) uniquement.
     */
    private async _deleteRestaurantImage(
        restaurantId: string,
        folder: 'cover' | 'dishes' | 'menu',
        storagePath: unknown,
    ): Promise<void> {
        const escapedId = restaurantId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const expected = new RegExp(`^restaurants/${escapedId}/${folder}/[A-Za-z0-9_-]+\\.jpg$`);
        if (typeof storagePath !== 'string' || !expected.test(storagePath)) return;
        try {
            const { sdk, storage } = await loadStorage();
            await sdk.deleteObject(sdk.ref(storage, storagePath));
        } catch (error: any) {
            if (error?.code !== 'storage/object-not-found') {
                console.warn('[Vitrine] Fichier non supprimé (orphelin) :', storagePath, error);
            }
        }
    }

    private _dishPhotosOf(data: Record<string, any> | undefined): RestaurantPhoto[] {
        return Array.isArray(data?.dishPhotos) ? data.dishPhotos : [];
    }

    /** Remplace la photo principale du lieu. `image` : JPEG déjà redimensionné (prepareImageForUpload). */
    async setRestaurantCoverPhoto(
        restaurantId: number | string,
        image: Blob,
    ): Promise<{ imageUrl: string; imageStoragePath: string }> {
        const rid = String(restaurantId);
        const restaurantRef = doc(db, 'restaurants', rid);
        let uploaded: { url: string; storagePath: string };
        try {
            uploaded = await this._uploadRestaurantImage(rid, 'cover', image);
        } catch (error) {
            throw this._mediaError(error, "L'envoi de la photo a échoué. Réessayez.");
        }

        let previousPath: unknown;
        try {
            await runTransaction(db, async (t) => {
                const snap = await t.get(restaurantRef);
                if (!snap.exists()) throw new UserFacingError('Restaurant introuvable.');
                previousPath = snap.data().imageStoragePath;
                t.update(restaurantRef, {
                    imageUrl: uploaded.url,
                    imageStoragePath: uploaded.storagePath,
                    updatedAt: serverTimestamp(),
                });
            });
        } catch (error) {
            await this._deleteRestaurantImage(rid, 'cover', uploaded.storagePath);
            throw this._mediaError(error, "La photo n'a pas pu être enregistrée. Réessayez.");
        }
        if (previousPath !== uploaded.storagePath) await this._deleteRestaurantImage(rid, 'cover', previousPath);
        this.notifyListeners();
        return { imageUrl: uploaded.url, imageStoragePath: uploaded.storagePath };
    }

    /** Retire la photo principale du lieu (document puis fichier). */
    async removeRestaurantCoverPhoto(restaurantId: number | string): Promise<void> {
        const rid = String(restaurantId);
        const restaurantRef = doc(db, 'restaurants', rid);
        let previousPath: unknown;
        try {
            await runTransaction(db, async (t) => {
                const snap = await t.get(restaurantRef);
                if (!snap.exists()) throw new UserFacingError('Restaurant introuvable.');
                previousPath = snap.data().imageStoragePath;
                t.update(restaurantRef, {
                    imageUrl: deleteField(),
                    imageStoragePath: deleteField(),
                    updatedAt: serverTimestamp(),
                });
            });
        } catch (error) {
            throw this._mediaError(error, "La photo n'a pas pu être retirée. Réessayez.");
        }
        await this._deleteRestaurantImage(rid, 'cover', previousPath);
        this.notifyListeners();
    }

    /** Ajoute une photo à la fin de la galerie « plats ». Retourne la galerie à jour. */
    async addRestaurantDishPhoto(restaurantId: number | string, image: Blob): Promise<RestaurantPhoto[]> {
        const rid = String(restaurantId);
        const restaurantRef = doc(db, 'restaurants', rid);
        const fullError = `Vous avez atteint le maximum de ${MAX_DISH_PHOTOS} photos de plats. Supprimez-en une pour en ajouter une autre.`;

        let uploaded: { id: string; url: string; storagePath: string };
        try {
            // Contrôle avant l'envoi pour ne pas téléverser inutilement (re-vérifié dans la transaction).
            const before = await getDoc(restaurantRef);
            if (this._dishPhotosOf(before.data()).length >= MAX_DISH_PHOTOS) throw new UserFacingError(fullError);
            uploaded = await this._uploadRestaurantImage(rid, 'dishes', image);
        } catch (error) {
            throw this._mediaError(error, "L'envoi de la photo a échoué. Réessayez.");
        }

        try {
            const photos = await runTransaction(db, async (t) => {
                const snap = await t.get(restaurantRef);
                if (!snap.exists()) throw new UserFacingError('Restaurant introuvable.');
                const current = this._dishPhotosOf(snap.data());
                if (current.length >= MAX_DISH_PHOTOS) throw new UserFacingError(fullError);
                const next: RestaurantPhoto[] = [
                    ...current,
                    { id: uploaded.id, url: uploaded.url, storagePath: uploaded.storagePath, createdAt: new Date().toISOString() },
                ];
                t.update(restaurantRef, { dishPhotos: next, updatedAt: serverTimestamp() });
                return next;
            });
            this.notifyListeners();
            return photos;
        } catch (error) {
            await this._deleteRestaurantImage(rid, 'dishes', uploaded.storagePath);
            throw this._mediaError(error, "La photo n'a pas pu être enregistrée. Réessayez.");
        }
    }

    /** Retire une photo de la galerie (document puis fichier). Retourne la galerie à jour. */
    async removeRestaurantDishPhoto(restaurantId: number | string, photoId: string): Promise<RestaurantPhoto[]> {
        const rid = String(restaurantId);
        const restaurantRef = doc(db, 'restaurants', rid);
        let removed: RestaurantPhoto | undefined;
        let photos: RestaurantPhoto[];
        try {
            photos = await runTransaction(db, async (t) => {
                const snap = await t.get(restaurantRef);
                if (!snap.exists()) throw new UserFacingError('Restaurant introuvable.');
                const current = this._dishPhotosOf(snap.data());
                removed = current.find((p) => p.id === photoId);
                if (!removed) return current; // déjà retirée (autre onglet)
                const next = current.filter((p) => p.id !== photoId);
                t.update(restaurantRef, { dishPhotos: next, updatedAt: serverTimestamp() });
                return next;
            });
        } catch (error) {
            throw this._mediaError(error, "La photo n'a pas pu être supprimée. Réessayez.");
        }
        if (removed) await this._deleteRestaurantImage(rid, 'dishes', removed.storagePath);
        this.notifyListeners();
        return photos;
    }

    /**
     * Réordonne la galerie selon `orderedIds`. Les photos absentes de la liste
     * (ajoutées entre-temps depuis un autre onglet) sont conservées à la fin.
     */
    async reorderRestaurantDishPhotos(restaurantId: number | string, orderedIds: string[]): Promise<RestaurantPhoto[]> {
        const restaurantRef = doc(db, 'restaurants', String(restaurantId));
        try {
            const photos = await runTransaction(db, async (t) => {
                const snap = await t.get(restaurantRef);
                if (!snap.exists()) throw new UserFacingError('Restaurant introuvable.');
                const current = this._dishPhotosOf(snap.data());
                const byId = new Map(current.map((p) => [p.id, p]));
                const ordered = orderedIds.map((id) => byId.get(id)).filter((p): p is RestaurantPhoto => !!p);
                const next = [...ordered, ...current.filter((p) => !orderedIds.includes(p.id))];
                t.update(restaurantRef, { dishPhotos: next, updatedAt: serverTimestamp() });
                return next;
            });
            this.notifyListeners();
            return photos;
        } catch (error) {
            throw this._mediaError(error, "Le nouvel ordre n'a pas pu être enregistré. Réessayez.");
        }
    }

    // ============================================
    // CARTE — PLATS ET PHOTOS DES PLATS (Firebase Storage)
    // ============================================
    // Fichiers : restaurants/{restaurantId}/menu/{id}.jpg, référencés par
    // menu[i].image (URL validée ; le chemin s'en déduit), cf. storage.rules.
    // Concurrence : la carte est un tableau du document restaurant. Chaque
    // opération est une transaction qui relit la carte enregistrée et ne modifie
    // que le plat concerné (src/shared/menuPhotos.ts) ; la photo n'est écrite que
    // par setMenuItemPhoto / removeMenuItemPhoto. Si deux opérations se croisent,
    // Firestore rejoue la transaction arrivée en second sur la carte à jour :
    // aucune des deux modifications n'est perdue.
    // Même ordre que la vitrine : envoi du fichier → transaction → suppression de
    // l'ancien fichier (s'il n'est plus utilisé par aucun plat).

    /** Plat ciblé par son identifiant : refus clair si le plat n'en a pas. */
    private _requireMenuItemId(itemId: unknown): asserts itemId is string {
        if (!isMenuItemId(itemId)) throw new UserFacingError(MENU_ITEM_ID_MISSING);
    }

    /** Supprime les photos de `candidates` qu'aucun plat de `menu` n'utilise plus. */
    private async _deleteUnusedMenuPhotos(restaurantId: string, candidates: (MenuItem | undefined)[], menu: MenuItem[]) {
        for (const path of unusedMenuPhotoPaths(restaurantId, candidates, menu)) {
            await this._deleteRestaurantImage(restaurantId, 'menu', path);
        }
    }

    /**
     * Applique `mutate` (null = rien à écrire) à la carte enregistrée, dans une
     * transaction. Les règles ne contrôlent que MAX_MENU_CHANGES_PER_WRITE plats
     * modifiés par écriture : au-delà, `tooManyMessage` plutôt qu'un refus brut.
     */
    private async _writeMenu(
        restaurantId: string,
        mutate: (current: MenuItem[]) => MenuItem[] | null,
        fallbackMessage: string,
        tooManyMessage = fallbackMessage,
    ): Promise<MenuItem[]> {
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        try {
            const menu = await runTransaction(db, async (t) => {
                const snap = await t.get(restaurantRef);
                if (!snap.exists()) throw new UserFacingError('Restaurant introuvable.');
                const current = menuOf(snap.data());
                const next = mutate(current);
                if (next === null) return current;
                if (changedMenuItems(next, current).length > MAX_MENU_CHANGES_PER_WRITE) {
                    throw new UserFacingError(tooManyMessage);
                }
                t.update(restaurantRef, { menu: next, updatedAt: serverTimestamp() });
                return next;
            });
            this.notifyListeners();
            return menu;
        } catch (error: any) {
            if (error instanceof Error && error.message === MENU_ITEM_ID_MISSING) {
                throw new UserFacingError(MENU_ITEM_ID_MISSING);
            }
            if (error?.code === 'permission-denied') {
                // Droits, mais aussi limite des règles (plats modifiés, expressions) : rien
                // ne permet de les distinguer, d'où un message qui ne présume pas de la cause.
                console.error('[Carte]', error);
                throw new UserFacingError("L'enregistrement a été refusé. Rechargez la page puis réessayez ; si le problème continue, contactez le support atable.");
            }
            throw this._mediaError(error, fallbackMessage);
        }
    }

    /** Ajoute (identifiant inconnu) ou modifie un plat, hors photo. Retourne la carte enregistrée. */
    async saveMenuItem(restaurantId: number | string, item: MenuItem): Promise<MenuItem[]> {
        this._requireMenuItemId(item.id);
        const rid = String(restaurantId);
        return this._writeMenu(
            rid,
            (current) => upsertMenuItem(rid, current, item),
            "Le plat n'a pas pu être enregistré. Vérifiez votre connexion puis réessayez.",
        );
    }

    /** Supprime un plat puis sa photo dans Storage (si aucun autre plat ne l'utilise). */
    async deleteMenuItem(restaurantId: number | string, itemId: string): Promise<MenuItem[]> {
        this._requireMenuItemId(itemId);
        const rid = String(restaurantId);
        let removed: MenuItem | undefined;
        const menu = await this._writeMenu(
            rid,
            (current) => {
                const result = removeMenuItem(current, itemId);
                removed = result.removed;
                return removed ? result.next : null; // déjà supprimé (autre onglet)
            },
            "Le plat n'a pas pu être supprimé. Vérifiez votre connexion puis réessayez.",
        );
        await this._deleteUnusedMenuPhotos(rid, [removed], menu);
        return menu;
    }

    /**
     * Ajoute ou remplace la photo d'un plat. `image` : JPEG déjà redimensionné
     * (prepareImageForUpload). Retourne la carte enregistrée.
     */
    async setMenuItemPhoto(restaurantId: number | string, itemId: string, image: Blob): Promise<MenuItem[]> {
        this._requireMenuItemId(itemId);
        const rid = String(restaurantId);
        const gone = "Ce plat a été supprimé entre-temps : la photo n'a pas été enregistrée.";
        let uploaded: { url: string; storagePath: string };
        try {
            // Contrôle avant l'envoi pour ne pas téléverser inutilement (re-vérifié dans la transaction).
            const before = await getDoc(doc(db, 'restaurants', rid));
            if (!menuOf(before.data()).some((m) => m.id === itemId)) throw new UserFacingError(gone);
            uploaded = await this._uploadRestaurantImage(rid, 'menu', image);
        } catch (error) {
            throw this._mediaError(error, "L'envoi de la photo a échoué. Réessayez.");
        }

        let previous: MenuItem | undefined;
        let menu: MenuItem[];
        try {
            menu = await this._writeMenu(
                rid,
                (current) => {
                    const result = setMenuItemPhoto(current, itemId, { image: uploaded.url });
                    if (!result) throw new UserFacingError(gone);
                    previous = result.previous;
                    return result.next;
                },
                "La photo n'a pas pu être enregistrée. Réessayez.",
            );
        } catch (error) {
            // Échec ambigu possible (réseau coupé après l'enregistrement) : le fichier
            // envoyé n'est supprimé que si la carte relue ne l'utilise pas.
            if (await this._menuPhotoUnused(rid, uploaded.storagePath)) {
                await this._deleteRestaurantImage(rid, 'menu', uploaded.storagePath);
            }
            throw error;
        }
        await this._deleteUnusedMenuPhotos(rid, [previous], menu);
        return menu;
    }

    /** true si la carte relue n'utilise pas ce fichier ; false si elle l'utilise ou ne peut être relue. */
    private async _menuPhotoUnused(restaurantId: string, storagePath: string): Promise<boolean> {
        try {
            const snap = await getDoc(doc(db, 'restaurants', restaurantId));
            return !menuOf(snap.data()).some((m) => menuPhotoPath(restaurantId, m) === storagePath);
        } catch (error) {
            console.warn('[Carte] Carte non relue : fichier envoyé conservé', storagePath, error);
            return false;
        }
    }

    /** Retire la photo d'un plat (document puis fichier). Retourne la carte enregistrée. */
    async removeMenuItemPhoto(restaurantId: number | string, itemId: string): Promise<MenuItem[]> {
        this._requireMenuItemId(itemId);
        const rid = String(restaurantId);
        let previous: MenuItem | undefined;
        const menu = await this._writeMenu(
            rid,
            (current) => {
                const result = setMenuItemPhoto(current, itemId, null);
                previous = result?.previous;
                // Plat supprimé ou déjà sans photo (autre onglet) : rien à écrire.
                return result && 'image' in result.previous ? result.next : null;
            },
            "La photo n'a pas pu être retirée. Réessayez.",
        );
        await this._deleteUnusedMenuPhotos(rid, [previous], menu);
        return menu;
    }

    /**
     * « Réparer la carte » : attribue un identifiant aux plats qui n'en ont pas (ou
     * en double), par lots de MAX_MENU_CHANGES_PER_WRITE (limite des règles).
     * Retourne la carte enregistrée.
     */
    async repairMenuItemIds(restaurantId: number | string): Promise<MenuItem[]> {
        const rid = String(restaurantId);
        let remaining = Infinity;
        let menu: MenuItem[] = [];
        for (let batch = 0; remaining > 0 && batch < 100; batch++) {
            menu = await this._writeMenu(
                rid,
                (current) => {
                    const result = repairMenuItemIds(rid, current);
                    remaining = result.remaining;
                    return result.repaired > 0 ? result.next : null;
                },
                "La carte n'a pas pu être réparée. Vérifiez votre connexion puis réessayez.",
            );
        }
        return menu;
    }

    // ============================================
    // LOYALTY
    // ============================================

    async getMemberships(userId: string) {
        try {
            const q = query(collection(db, 'memberships'), where('userId', '==', userId));
            const snapshot = await getDocs(q);
            return this._dedupMemberships(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error('Error fetching memberships:', error);
            return [];
        }
    }

    /**
     * Subscribe temps réel aux memberships du user. Utilisé par l'app cliente
     * (LoyerLoyalty/LoyerLoyaltyScreen) pour voir les points crédités sans refresh.
     */
    subscribeToUserMemberships(userId: string, callback: (memberships: any[]) => void): () => void {
        const q = query(collection(db, 'memberships'), where('userId', '==', userId));
        return onSnapshot(
            q,
            (snap) => {
                const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                callback(this._dedupMemberships(all));
            },
            (err) => console.error('[onSnapshot memberships]', err),
        );
    }

    /**
     * Dédup par restaurantId : si plusieurs memberships existent pour un même couple
     * (userId, restaurantId) en base — cas des splits historiques entre legacy auto-id
     * et l'id déterministe — on garde celui avec le plus de points/stamps.
     */
    private _dedupMemberships(memberships: any[]): any[] {
        const byResto = new Map<string, any>();
        for (const m of memberships) {
            const key = String(m.restaurantId);
            const existing = byResto.get(key);
            if (!existing) { byResto.set(key, m); continue; }
            const score = (m.points ?? 0) + (m.stamps ?? 0);
            const existingScore = (existing.points ?? 0) + (existing.stamps ?? 0);
            if (score > existingScore) byResto.set(key, m);
        }
        return Array.from(byResto.values());
    }

    async getRestaurantCustomers(restaurantId: number | string): Promise<Customer[]> {
        try {
            const restIdStr = String(restaurantId);

            const memQ = query(
                collection(db, 'memberships'),
                where('restaurantId', '==', restIdStr)
            );
            const memSnap = await getDocs(memQ);
            if (memSnap.empty) return [];

            // Toutes les visites du resto en une requête.
            const visitsQ = query(collection(db, 'visits'), where('restaurantId', '==', restIdStr));
            const visitsSnap = await getDocs(visitsQ);
            const allVisits = visitsSnap.docs.map(d => ({ ...d.data() }));

            // Tous les tickets POS du resto.
            const ordersQ = query(collection(db, 'posOrders'), where('restaurantId', '==', restIdStr));
            const ordersSnap = await getDocs(ordersQ);
            const allOrders = ordersSnap.docs.map(d => ({ ...d.data() }));

            const now = new Date();
            const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
            const cutoff90 = new Date(now.getTime() - NINETY_DAYS_MS);

            const toDate = (raw: any): Date | null => {
                if (!raw) return null;
                if (raw?.toDate) return raw.toDate();
                if (typeof raw === 'string') {
                    const d = new Date(raw);
                    return Number.isNaN(d.getTime()) ? null : d;
                }
                return null;
            };

            // Segments calculés chaque nuit par la Cloud Function (lecture restaurateur uniquement)
            const segmentsByUser = new Map<string, { segment: CustomerSegmentKey | null; stats?: CustomerSegmentStats }>();
            try {
                const segQ = query(collection(db, 'customerSegments'), where('restaurantId', '==', restIdStr));
                const segSnap = await getDocs(segQ);
                segSnap.docs.forEach(d => {
                    const data = d.data() as { userId?: unknown; segment?: unknown; stats?: CustomerSegmentStats };
                    const seg = (SEGMENT_KEYS as string[]).includes(String(data.segment)) ? (data.segment as CustomerSegmentKey) : null;
                    segmentsByUser.set(String(data.userId), { segment: seg, stats: data.stats });
                });
            } catch (e) {
                console.warn('customerSegments non lisibles (règles non déployées ?)', e);
            }

            // Dédoublonnage : un même userId peut avoir plusieurs memberships en base
            // (legacy auto-id + nouvel id déterministe). On garde celui avec le plus de
            // points/stamps (= le plus à jour fonctionnellement). Évite que le client
            // apparaisse en double dans la liste.
            const memByUserId = new Map<string, typeof memSnap.docs[number]>();
            for (const d of memSnap.docs) {
                const m = d.data();
                const uid = m.userId;
                if (!uid) continue;
                const existing = memByUserId.get(uid);
                if (!existing) {
                    memByUserId.set(uid, d);
                    continue;
                }
                const score = (m.points ?? 0) + (m.stamps ?? 0);
                const existingScore = (existing.data().points ?? 0) + (existing.data().stamps ?? 0);
                if (score > existingScore) memByUserId.set(uid, d);
            }

            const customers: Customer[] = [];
            for (const d of Array.from(memByUserId.values())) {
                const mem = d.data();
                const userId = mem.userId;

                // Profil user (best-effort).
                let userObj: any = { firstName: 'Client', lastName: 'Inconnu', avatarUrl: undefined };
                try {
                    const uSnap = await getDoc(doc(db, 'users', userId));
                    if (uSnap.exists()) userObj = { ...userObj, ...uSnap.data() };
                } catch { /* ignore */ }

                // Visites du user chez ce resto.
                const userVisits = allVisits
                    .filter((v: any) => v.userId === userId)
                    .map((v: any) => ({ ...v, _date: toDate(v.createdAt) }))
                    .filter((v: any) => v._date instanceof Date) as Array<any & { _date: Date }>;

                const totalVisits = userVisits.length;

                // Dernière visite.
                let lastVisitDate: Date | null = null;
                if (totalVisits > 0) {
                    lastVisitDate = userVisits
                        .map((v: any) => v._date as Date)
                        .reduce((a, b) => (a.getTime() > b.getTime() ? a : b));
                }
                const daysSinceLastVisit = lastVisitDate
                    ? Math.floor((now.getTime() - lastVisitDate.getTime()) / (24 * 60 * 60 * 1000))
                    : null;

                const visitsLast90Days = userVisits.filter(v => v._date >= cutoff90).length;

                // Panier moyen / total dépensé (euros) : on ne calcule QUE si au moins un visit a un `amount` peuplé.
                // Sinon on retourne null pour que l'UI puisse masquer (cf. spec : pas de 0,00 € mensonger).
                // `visits.amount` est en centimes (visitAmountCents gère les anciennes visites de caisse en euros).
                const visitsWithAmount = userVisits.filter(v => visitAmountCents(v) !== null);
                let averageBasket: number | null = null;
                let totalSpent: number | null = null;
                if (visitsWithAmount.length > 0) {
                    const sum = visitsWithAmount.reduce((s, v) => s + visitAmountCents(v)! / 100, 0);
                    totalSpent = Math.round(sum * 100) / 100;
                    averageBasket = Math.round((sum / visitsWithAmount.length) * 100) / 100;
                }

                // Top plats : agrège depuis (a) `visit.items` — capturés à la complétion
                // de réservation par les CFs B2 — et (b) `posOrders.items` quand
                // `posOrders.customerId` est peuplé. La 1re source est la plus fiable
                // aujourd'hui (la 2e dépend du POS, peu utilisé en pilote).
                const userOrders = allOrders.filter((o: any) => o.customerId === userId);
                const dishCount: Record<string, number> = {};
                for (const v of userVisits) {
                    for (const item of ((v as any).items || [])) {
                        const name = (item.name as string | undefined)?.trim();
                        if (!name) continue;
                        dishCount[name] = (dishCount[name] || 0) + (Number(item.quantity) || 1);
                    }
                }
                for (const order of userOrders) {
                    for (const item of ((order as any).items || [])) {
                        const name = (item.name as string | undefined)?.trim();
                        if (!name) continue;
                        dishCount[name] = (dishCount[name] || 0) + (Number(item.quantity) || 1);
                    }
                }
                const topDishes = Object.entries(dishCount)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3)
                    .map(([name]) => name);

                // Visites par mois (12 derniers mois) — utilisé par fiche client si ≥ 2 mois actifs.
                const visitsByMonth: { month: string; visits: number }[] = [];
                for (let i = 11; i >= 0; i--) {
                    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                    const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    const monthLabel = d.toLocaleDateString('fr-FR', { month: 'short' });
                    const monthCount = userVisits.filter(v => {
                        const vd = v._date;
                        return `${vd.getFullYear()}-${String(vd.getMonth() + 1).padStart(2, '0')}` === monthKey;
                    }).length;
                    visitsByMonth.push({ month: monthLabel, visits: monthCount });
                }

                const joinedDate = mem.joinedDate?.toDate
                    ? mem.joinedDate.toDate().toISOString()
                    : (typeof mem.joinedDate === 'string' ? mem.joinedDate : undefined);

                customers.push({
                    id: userId,
                    name: `${userObj.firstName ?? ''} ${userObj.lastName ?? ''}`.trim() || 'Client',
                    avatarUrl: safeStorageImageUrl(userObj.avatarUrl) ?? undefined,
                    segment: segmentsByUser.get(userId)?.segment ?? null,
                    segmentStats: segmentsByUser.get(userId)?.stats,
                    totalVisits,
                    visitsLast90Days,
                    lastVisitDate: lastVisitDate ? lastVisitDate.toISOString() : null,
                    daysSinceLastVisit,
                    joinedDate,
                    averageBasket,
                    totalSpent,
                    loyaltyScore: typeof mem.points === 'number' ? mem.points : 0,
                    visitsByMonth,
                    topDishes,
                });
            }
            return customers;
        } catch (error) {
            console.error('Error fetching customers:', error);
            return [];
        }
    }

    /**
     * Programme de fidélité : `loyaltyPrograms/{restaurantId}`, le seul document lu par les
     * Cloud Functions et l'app mobile. Repli, pour les restaurants créés avant ce correctif,
     * sur un ancien document à identifiant automatique (requête par champ) — recopié vers
     * `{restaurantId}` au prochain enregistrement (et par scripts/migrate-loyalty-programs.mjs).
     * Lève une erreur si la lecture échoue : l'écran ne doit pas publier sur un état inconnu.
     */
    async getLoyaltyProgram(restaurantId: number | string) {
        const restIdStr = String(restaurantId);
        const own = await getDoc(doc(db, 'loyaltyPrograms', restIdStr));
        if (own.exists() && String((own.data() as any).restaurantId) === restIdStr) {
            return { id: own.id, ...own.data() };
        }
        const snapshot = await getDocs(query(collection(db, 'loyaltyPrograms'), where('restaurantId', '==', restIdStr)));
        if (snapshot.empty) return null;
        // Le plus récemment modifié s'il en reste plusieurs.
        const ms = (v: any) => (v?.toMillis ? v.toMillis() : 0);
        const d = snapshot.docs.reduce((a, b) => (ms((b.data() as any).updatedAt) > ms((a.data() as any).updatedAt) ? b : a));
        return { id: d.id, ...d.data() };
    }

    /**
     * Sauvegarde (création ou mise à jour) du programme de fidélité dans Firestore.
     * Retourne l'ancien type si un programme existait (utile pour la migration).
     */
    async saveLoyaltyProgram(restaurantId: number | string, config: any): Promise<{ previousType: string | null }> {
        const restIdStr = String(restaurantId);
        try {
            const existing = await this.getLoyaltyProgram(restaurantId);
            const previousType = existing ? (existing as any).type : null;

            // Firestore refuse les valeurs `undefined`. On nettoie récursivement le payload
            // V2 avant écriture pour éviter "Unsupported field value: undefined" sur les
            // champs optionnels non renseignés (boost.initialStamps, advantage.value, etc.).
            const stripUndefined = (input: any): any => {
                if (Array.isArray(input)) return input.map(stripUndefined);
                if (input && typeof input === 'object') {
                    const out: Record<string, any> = {};
                    for (const [k, v] of Object.entries(input)) {
                        if (v === undefined) continue;
                        out[k] = stripUndefined(v);
                    }
                    return out;
                }
                return input;
            };

            const programData: Record<string, any> = {
                restaurantId: restIdStr,
                type: config.type,
                spendingRatio: config.spendingRatio || null,
                targetCount: config.targetCount || null,
                targetSpending: config.targetSpending || null,
                welcomeBonus: config.welcomeBonus || 0,
                rewardLabel: config.rewardLabel || '',
                missions: config.missions || [],
                updatedAt: serverTimestamp(),
            };
            // V2 (B1) — persistance des nouveaux champs si présents.
            // Le runtime de crédit ne les consomme PAS encore (B2 prendra le relais).
            if (config.generosity !== undefined) programData.generosity = config.generosity;
            if (Array.isArray(config.rewardTiers)) programData.rewardTiers = stripUndefined(config.rewardTiers);
            if (Array.isArray(config.grades)) programData.grades = stripUndefined(config.grades);
            if (config.boost && typeof config.boost === 'object') programData.boost = stripUndefined(config.boost);

            // Changement de type : la conversion des membres (Cloud Function
            // migrateProgramMemberships) mesure la progression sur l'objectif réel de l'ancien
            // programme ; `pendingMigrationFrom` reste posé tant qu'elle n'a pas abouti
            // (l'écran propose alors « Relancer la conversion »).
            if (previousType && previousType !== config.type) {
                const prev = existing as any;
                const tiers = Array.isArray(prev.rewardTiers)
                    ? prev.rewardTiers.map((t: any) => Number(t?.threshold)).filter((n: number) => n > 0) : [];
                const goal = Number(prev.targetCount) > 0 ? Number(prev.targetCount)
                    : tiers.length ? Math.min(...tiers) : (previousType === 'stamps' ? 10 : 100);
                programData.previousProgram = { type: previousType, goal, changedAt: serverTimestamp() };
                programData.pendingMigrationFrom = previousType;
            }

            // Toujours `loyaltyPrograms/{restaurantId}` (règles : identifiant = restaurantId à la
            // création). Un ancien document à identifiant automatique est laissé en place (le
            // script de migration le signale) mais n'est plus lu une fois celui-ci créé.
            const ownExists = !!existing && (existing as any).id === restIdStr;
            await setDoc(doc(db, 'loyaltyPrograms', restIdStr), {
                ...programData,
                ...(ownExists ? {} : { createdAt: serverTimestamp() }),
            }, { merge: true });

            this.notifyListeners();
            return { previousType };
        } catch (error: any) {
            console.error('Error saving loyalty program:', error);
            throw new Error(error.message || 'Erreur de sauvegarde du programme');
        }
    }

    /**
     * Après un changement de type de programme (déjà enregistré par saveLoyaltyProgram) :
     * conversion de la progression des membres par la Cloud Function
     * migrateProgramMemberships. Le serveur relit le nouveau type et l'objectif dans le
     * programme enregistré ; seul l'ancien type est transmis. Les adhésions ne sont plus
     * modifiables depuis le navigateur (règles Firestore).
     * `restaurantId`, `newType` et `config` sont gardés pour la compatibilité des appelants.
     */
    async migrateMemberships(
        _restaurantId: number | string,
        oldType: string,
        _newType?: string,
        _config?: unknown,
    ): Promise<{ migratedCount: number }> {
        try {
            const fn = httpsCallable<{ fromType: string }, { migratedCount: number; fromType: string; toType: string }>(
                this._fns, 'migrateProgramMemberships',
            );
            const { data } = await fn({ fromType: oldType });
            this.notifyListeners();
            return { migratedCount: data.migratedCount };
        } catch (error: any) {
            console.error('Error migrating memberships:', error);
            const reason = callableErrorReason(error);
            throw new Error(reason === 'no_response'
                ? 'Programme enregistré, mais la conversion des points de vos clients n\'a pas répondu : rechargez la page pour vérifier.'
                : checkinMessage(reason));
        }
    }

    // ============================================
    // WALLET QR — Sessions éphémères client
    // ============================================

    // ── Cloud Function helpers ────────────────────────────────────────────────

    private _fns = getFunctions(app);

    /**
     * Génère une session QR via Cloud Function.
     * Le nonce est créé côté serveur (crypto.randomUUID), signé HMAC-SHA256.
     * L'userId est extrait du JWT Firebase côté serveur — jamais passé en paramètre.
     */
    async generateQrSession(): Promise<{ qrPayload: string; expiresAt: number }> {
        const fn = httpsCallable<Record<string, never>, { qrPayload: string; expiresAt: number }>(
            this._fns,
            'generateQrSession',
        );
        const result = await fn({});
        return result.data;
    }

    /**
     * Valide le QR wallet d'un client via Cloud Function — moyen de secours quand le NFC
     * ne marche pas. Même logique serveur que le tap NFC (verrou 1 validation / client /
     * resto / jour, prix de la carte, plafonds). Le restaurantId est extrait du JWT
     * restaurateur côté serveur — jamais passé en paramètre.
     *
     * `pendingTransactionId` : scan depuis l'écran d'attente d'une addition (« Encaissement
     * fidélité ») → c'est cette addition qui est reliée au client.
     */
    async validateQrSession(
        nonce: string,
        options?: {
            pendingTransactionId?: string;
            linkedReservationId?: string;
            amountCents?: number;
            dishes?: Array<{ menuItemId?: string; name: string; quantity: number; unitPrice: number }>;
        },
    ): Promise<{
        success: boolean;
        pointsEarned: number;
        stampsEarned?: number;
        programType?: string;
        linked?: 'pending' | 'reservation' | 'none';
        amountCents?: number;
        clientName?: string;
        message?: string;
    }> {
        if (!navigator.onLine) {
            return { success: false, pointsEarned: 0, message: 'offline' };
        }

        try {
            const fn = httpsCallable<
                {
                    nonce: string;
                    pendingTransactionId?: string;
                    linkedReservationId?: string;
                    amountCents?: number;
                    dishes?: Array<{ menuItemId?: string; name: string; quantity: number; unitPrice: number }>;
                },
                {
                    success: boolean;
                    pointsEarned?: number;
                    stampsEarned?: number;
                    programType?: string;
                    linked?: 'pending' | 'reservation' | 'none';
                    amountCents?: number;
                    clientName?: string;
                    message?: string;
                }
            >(this._fns, 'validateQrSession');

            const result = await fn({
                nonce,
                ...(options?.pendingTransactionId ? { pendingTransactionId: options.pendingTransactionId } : {}),
                ...(options?.linkedReservationId ? { linkedReservationId: options.linkedReservationId } : {}),
                ...(options?.amountCents !== undefined ? { amountCents: options.amountCents } : {}),
                ...(options?.dishes ? { dishes: options.dishes } : {}),
            });
            const data = result.data;

            if (data.success) {
                this.notifyListeners();
            }

            return {
                success: data.success,
                pointsEarned: data.pointsEarned ?? 0,
                stampsEarned: data.stampsEarned,
                programType: data.programType,
                linked: data.linked,
                amountCents: data.amountCents,
                clientName: data.clientName,
                message: data.message,
            };
        } catch (error: any) {
            return { success: false, pointsEarned: 0, message: callableErrorReason(error) };
        }
    }

    // ============================================
    // NFC POS — Cloud Functions
    // ============================================

    /**
     * Puces NFC signées du restaurant : génère `count` puces et renvoie l'URL à programmer
     * sur chacune ({TAG_BASE_URL}/t?n=…&s=…, propre au projet Firebase).
     */
    async generateNfcBatch(count: number, restaurantId: string, label?: string): Promise<{
        tokens: Array<{ nonce: string; url: string; label: string }>;
        batchId: string;
    }> {
        const fn = httpsCallable<{ count: number; restaurantId: string; label?: string }, {
            tokens: Array<{ nonce: string; url: string; label: string }>;
            batchId: string;
        }>(this._fns, 'generateNfcBatch');
        const result = await fn({ count, restaurantId, ...(label ? { label } : {}) });
        return result.data;
    }

    /**
     * Désactive une puce (perdue, abîmée, copiée) : elle ne valide plus aucun repas.
     */
    async revokeNfcToken(nonce: string, restaurantId: string): Promise<void> {
        const fn = httpsCallable<{ nonce: string; restaurantId: string }, { success: boolean }>(
            this._fns, 'revokeNfcToken',
        );
        await fn({ nonce, restaurantId });
        this.notifyListeners();
    }

    /**
     * Puces du restaurant, avec l'URL à programmer (recalculée par le serveur).
     */
    async getNfcTokens(restaurantId: string): Promise<NfcTagInfo[]> {
        const fn = httpsCallable<{ restaurantId: string }, { tokens: NfcTagInfo[] }>(this._fns, 'getNfcTokens');
        const result = await fn({ restaurantId });
        return result.data.tokens;
    }

    /**
     * Récupère l'audit trail d'un restaurant.
     */
    async getAudits(restaurantId: string, limit = 50): Promise<Array<Record<string, unknown>>> {
        const fn = httpsCallable<{ restaurantId: string; limit: number }, { audits: Array<Record<string, unknown>> }>(
            this._fns, 'getAudits',
        );
        const result = await fn({ restaurantId, limit });
        return result.data.audits;
    }

    // ============================================
    // PENDING TRANSACTIONS — Flow V2
    // ============================================

    /**
     * Restaurateur → prépare une addition (montant + plats de la carte, optionnels).
     * Le client la relie ensuite : tap NFC + code à 6 chiffres (`code`, renvoyé une seule
     * fois, à afficher sur l'écran d'attente), ou QR scanné depuis l'écran d'attente.
     * Les plats hors carte ne sont pas envoyés : ils ne comptent que dans le montant.
     */
    async createPendingTransaction(data: {
        restaurantId: string;
        amount: number;
        items?: Array<{ menuItemId: string; quantity: number }>;
        linkedReservationId?: string;
    }): Promise<{ pendingTransactionId: string; expiresAt: number; code?: string }> {
        const fn = httpsCallable<typeof data, { pendingTransactionId: string; expiresAt: number; code?: string }>(
            this._fns, 'createPendingTransaction',
        );
        const result = await fn(data);
        return result.data;
    }

    /**
     * Bouton « Annuler » de l'écran d'attente : l'addition ne peut plus être reliée.
     * `cancelled: false` si le client l'a reliée entre-temps (status 'claimed').
     */
    async cancelPendingTransaction(pendingTransactionId: string): Promise<{ cancelled: boolean; status: string }> {
        const fn = httpsCallable<{ pendingTransactionId: string }, { cancelled: boolean; status: string }>(
            this._fns, 'cancelPendingTransaction',
        );
        const result = await fn({ pendingTransactionId });
        return result.data;
    }

    /**
     * « Validation manuelle » : le serveur valide le repas d'un client inscrit (même verrou
     * du jour que le tap et le QR, motif journalisé). Plus aucune écriture directe.
     */
    async validateManualCheckin(clientId: string, reason: string): Promise<{
        success: boolean;
        pointsEarned: number;
        stampsEarned?: number;
        programType?: string;
        clientName?: string;
        message?: string;
    }> {
        try {
            const fn = httpsCallable<{ clientId: string; reason: string }, {
                success: boolean; pointsEarned?: number; stampsEarned?: number; programType?: string;
                clientName?: string; message?: string;
            }>(this._fns, 'validateManualCheckin');
            const { data } = await fn({ clientId, reason });
            if (data.success) this.notifyListeners();
            return { ...data, pointsEarned: data.pointsEarned ?? 0 };
        } catch (error: any) {
            return { success: false, pointsEarned: 0, message: callableErrorReason(error) };
        }
    }

    /**
     * Rattrape les rewards manquantes pour le user courant (paliers atteints AVANT
     * que la chaîne B2 soit déployée, ou crashes partiels). Idempotent — peut être
     * appelée librement au mount du wallet.
     */
    async ensureRewardsForUser(restaurantId?: string): Promise<{
        success: boolean;
        rewardsCreated: number;
        membershipsScanned?: number;
        debug?: any[];
    }> {
        const fn = httpsCallable<{ restaurantId?: string }, {
            success: boolean;
            rewardsCreated: number;
            membershipsScanned?: number;
            debug?: any[];
        }>(this._fns, 'ensureRewardsForUser');
        const { data } = await fn(restaurantId ? { restaurantId } : {});
        return data;
    }

    /**
     * Subscribe en temps réel aux rewards non utilisées d'un user, tous restos confondus.
     * Alimente l'onglet "Récompenses" du LoyerLoyalty (web + mobile via les CFs B2 qui
     * créent des docs `rewards` quand un palier `program.rewardTiers[]` est franchi).
     */
    subscribeToUserRewards(userId: string, callback: (rewards: Reward[]) => void): () => void {
        const q = query(
            collection(db, 'rewards'),
            where('userId', '==', userId),
            where('used', '==', false),
        );
        return onSnapshot(
            q,
            (snap) => {
                const rewards: Reward[] = snap.docs.map((d) => {
                    const data = d.data() as Record<string, any>;
                    return {
                        id: d.id,
                        description: data.description || data.label || `Palier ${data.tierThreshold ?? ''}`.trim(),
                        type: data.type,
                        cost: data.cost,
                        isRealTime: data.isRealTime,
                        expiry: data.expiry,
                        restaurantId: data.restaurantId != null ? String(data.restaurantId) : undefined,
                        userId: data.userId,
                        tierThreshold: data.tierThreshold,
                        used: data.used === true,
                        createdAt:
                            typeof data.createdAt?.toDate === 'function'
                                ? data.createdAt.toDate().toISOString()
                                : (typeof data.createdAt === 'string' ? data.createdAt : undefined),
                    };
                });
                callback(rewards);
            },
            (err) => console.error('[onSnapshot rewards]', err),
        );
    }

    /**
     * Listener temps réel sur une pending transaction.
     * Utilisé par le restaurateur pour voir la confirmation sans polling.
     * Retourne la fonction d'unsubscribe.
     */
    onPendingTransactionUpdate(
        txId: string,
        callback: (data: Record<string, unknown> & { id: string }) => void,
    ): () => void {
        const ref = doc(db, 'pendingTransactions', txId);
        return onSnapshot(ref, (snap) => {
            if (snap.exists()) {
                callback({ id: snap.id, ...(snap.data() as Record<string, unknown>) });
            }
        });
    }

    /**
     * Garde-fou de la saisie du code d'addition du restaurant (billCodeGuards) : trop de codes
     * erronés → saisie suspendue jusqu'à `suspendedUntil` (ms). 0 si aucune suspension.
     * Les additions ne sont pas bloquées : le QR reste utilisable.
     */
    onBillCodeGuardUpdate(restaurantId: string, callback: (suspendedUntil: number) => void): () => void {
        return onSnapshot(
            doc(db, 'billCodeGuards', restaurantId),
            (snap) => callback(snap.exists() ? Number((snap.data() as any).suspendedUntil) || 0 : 0),
            () => callback(0),
        );
    }

    /**
     * Page /t des clients sans l'app (guestCodeGuards) : trop de codes faux → « sous protection »
     * jusqu'à `protectedUntil` (ms) : les visiteurs résolvent un défi de quelques secondes avant
     * chaque essai (rien n'est bloqué). 0 si aucune protection.
     */
    onGuestCodeGuardUpdate(restaurantId: string, callback: (protectedUntil: number) => void): () => void {
        return onSnapshot(
            doc(db, 'guestCodeGuards', restaurantId),
            (snap) => callback(snap.exists() ? Number((snap.data() as any).protectedUntil) || 0 : 0),
            () => callback(0),
        );
    }

    // ============================================
    // CAMPAIGNS
    // ============================================

    async getCampaigns(restaurantId: number | string): Promise<Campaign[]> {
        try {
            const q = query(
                collection(db, 'campaigns'),
                where('restaurantId', '==', String(restaurantId))
            );
            const snapshot = await getDocs(q);
            const campaigns = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as any));
            return campaigns.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        } catch (error) {
            console.error('Error fetching campaigns:', error);
            return [];
        }
    }

    // ── Segmentation client (restaurateur uniquement) ─────────────────────

    /** Seuils de segmentation du restaurant (fusionnés avec les défauts) + date du dernier calcul. */
    async getSegmentRules(restaurantId: number | string): Promise<SegmentRulesDoc> {
        const { mergeRules } = await import('../customerSegmentation');
        try {
            const snap = await getDoc(doc(db, 'segmentRules', String(restaurantId)));
            const data = snap.exists() ? (snap.data() as any) : {};
            return {
                rules: mergeRules(data),
                lastComputedAt: data.lastComputedAt?.toDate ? data.lastComputedAt.toDate() : null,
                lastCounts: data.lastCounts,
            };
        } catch (error) {
            console.error('Error fetching segment rules:', error);
            return { rules: mergeRules(null), lastComputedAt: null, loadFailed: true };
        }
    }

    async saveSegmentRules(restaurantId: number | string, rules: SegmentRules): Promise<void> {
        await setDoc(
            doc(db, 'segmentRules', String(restaurantId)),
            { ...rules, updatedAt: serverTimestamp() },
            { merge: true },
        );
    }

    /** Recalcule les segments tout de suite (Cloud Function, 10 appels/heure max). */
    async recomputeSegments(restaurantId: number | string): Promise<{ members: number; changed: number; counts: Record<string, number> }> {
        const fn = httpsCallable<{ restaurantId: string }, { members: number; changed: number; counts: Record<string, number> }>(
            this._fns,
            'recomputeSegmentsNow',
        );
        const res = await fn({ restaurantId: String(restaurantId) });
        this.notifyListeners();
        return res.data;
    }

    async createCampaign(campaign: Omit<Campaign, 'id' | 'stats' | 'createdAt'>): Promise<void> {
        try {
            await addDoc(collection(db, 'campaigns'), {
                ...campaign,
                restaurantId: String(campaign.restaurantId),
                stats: { openRate: 0, conversionRate: 0, revenue: 0 },
                createdAt: serverTimestamp(),
            });

            // Push manuel, ou lancement d'une vente flash (une seule fois : cf. flashCampaignDraft).
            if (campaign.type === 'Push' || campaign.flashPromoId) {
                try {
                    const sendPush = httpsCallable(this._fns, 'sendCampaignPush');
                    await sendPush({
                        restaurantId: String(campaign.restaurantId),
                        title: campaign.name,
                        body: campaign.description || campaign.name,
                        segment: campaign.targetSegment || 'all',
                    });
                } catch (pushErr) {
                    console.error('Push notification failed (campaign saved anyway):', pushErr);
                }
            }

            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de création campagne');
        }
    }

    async getFlashPromotions(restaurantId: number | string): Promise<FlashPromotion[]> {
        try {
            const q = query(
                collection(db, 'flashPromotions'),
                where('restaurantId', '==', String(restaurantId))
            );
            const snapshot = await getDocs(q);
            const promos = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as any));
            return promos.filter(p => p.active);
        } catch (error) {
            console.error('Error fetching flash promotions:', error);
            return [];
        }
    }

    async getAllActiveFlashPromotions(): Promise<FlashPromotion[]> {
        try {
            const q = query(
                collection(db, 'flashPromotions'),
                where('active', '==', true),
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as any));
        } catch (error) {
            console.error('Error fetching flash promotions:', error);
            return [];
        }
    }

    async createFlashPromotion(promo: Omit<FlashPromotion, 'id' | 'createdAt'>): Promise<FlashPromotion> {
        try {
            const docRef = await addDoc(collection(db, 'flashPromotions'), {
                ...promo,
                restaurantId: String(promo.restaurantId),
                date: promo.date || todayISO(),
                quantityRemaining: promo.quantityTotal,
                active: true,
                createdAt: serverTimestamp(),
            });

            // Campagne liée à la vente : c'est elle qui prévient les membres du segment choisi.
            await this.createCampaign(flashCampaignDraft(promo, docRef.id) as any);

            this.notifyListeners();
            return { id: docRef.id, ...promo, createdAt: new Date().toISOString() };
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de création promotion flash');
        }
    }

    // Toutes les ventes flash d'un restaurant (actives ou non), plus récentes d'abord.
    async getRestaurantFlashPromotions(restaurantId: number | string): Promise<FlashPromotion[]> {
        const snapshot = await getDocs(query(
            collection(db, 'flashPromotions'),
            where('restaurantId', '==', String(restaurantId)),
        ));
        return snapshot.docs
            .map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as FlashPromotion))
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    // Campagne(s) générée(s) automatiquement par createFlashPromotion.
    private async _getFlashPromoCampaigns(restaurantId: string, promoId: string) {
        const snapshot = await getDocs(query(
            collection(db, 'campaigns'),
            where('restaurantId', '==', restaurantId),
            where('flashPromoId', '==', promoId),
        ));
        return snapshot.docs;
    }

    async updateFlashPromotion(
        id: string,
        changes: Pick<FlashPromotion, 'menuItemId' | 'itemName' | 'originalPrice' | 'discountPrice'
            | 'quantityTotal' | 'date' | 'startTime' | 'endTime' | 'targetSegment'>,
    ): Promise<void> {
        const ref = doc(db, 'flashPromotions', id);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error('Vente flash introuvable');
        const current = snap.data() as FlashPromotion;

        // Conserve le nombre déjà vendu quand la quantité totale change.
        const sold = Math.max(0, (current.quantityTotal || 0) - (current.quantityRemaining ?? current.quantityTotal ?? 0));
        // Enregistrer une vente arrêtée ou terminée la relance.
        await updateDoc(ref, {
            ...changes,
            quantityRemaining: Math.max(0, changes.quantityTotal - sold),
            active: true,
            updatedAt: serverTimestamp(),
        });

        for (const campaign of await this._getFlashPromoCampaigns(String(current.restaurantId), id)) {
            await updateDoc(campaign.ref, {
                status: 'active',
                name: `⚡ Flash : ${changes.itemName}`,
                description: `Vente flash sur ${changes.itemName} ! ${changes.discountPrice}€ au lieu de ${changes.originalPrice}€.`,
                targetSegment: changes.targetSegment || 'all',
            });
        }
        this.notifyListeners();
    }

    async stopFlashPromotion(id: string): Promise<void> {
        const ref = doc(db, 'flashPromotions', id);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error('Vente flash introuvable');
        await updateDoc(ref, { active: false, updatedAt: serverTimestamp() });

        for (const campaign of await this._getFlashPromoCampaigns(String(snap.data().restaurantId), id)) {
            await updateDoc(campaign.ref, { status: 'ended' });
        }
        this.notifyListeners();
    }

    async deleteFlashPromotion(id: string): Promise<void> {
        const ref = doc(db, 'flashPromotions', id);
        const snap = await getDoc(ref);
        if (!snap.exists()) return;

        for (const campaign of await this._getFlashPromoCampaigns(String(snap.data().restaurantId), id)) {
            await deleteDoc(campaign.ref);
        }
        await deleteDoc(ref);
        this.notifyListeners();
    }

    // Listener temps réel pour les flash promos (côté client)
    subscribeToFlashPromotions(callback: (promos: FlashPromotion[]) => void) {
        const q = query(
            collection(db, 'flashPromotions'),
            where('active', '==', true),
        );
        return onSnapshot(q, (snapshot) => {
            const promos = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as any));
            callback(promos);
        });
    }

    // ============================================
    // RESERVATIONS
    // ============================================

    async createReservation(res: Omit<Reservation, 'id' | 'createdAt'>): Promise<Reservation> {
        try {
            const data = {
                ...res,
                restaurantId: String(res.restaurantId),
                status: 'confirmed',
                createdAt: serverTimestamp(),
            };
            const flashPromoId = (res as { flashPromoId?: string }).flashPromoId;
            const id = flashPromoId
                ? await this._reserveFlashPromotion(flashPromoId, data)
                : (await addDoc(collection(db, 'reservations'), data)).id;
            this.notifyListeners();
            return {
                id,
                ...res,
                status: 'confirmed',
                createdAt: new Date().toISOString(),
            };
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de création réservation');
        }
    }

    /**
     * Réserve une offre flash : retire 1 plat du stock et crée la réservation
     * dans la même transaction (deux clients ne peuvent pas prendre le dernier plat).
     * Id {promoId}_{uid} : une seule réservation par client et par offre (imposé par les règles).
     * Copie côté mobile : apps/mobile/src/components/BookingModal.tsx.
     */
    private async _reserveFlashPromotion(promoId: string, data: Record<string, unknown>): Promise<string> {
        const uid = auth.currentUser?.uid;
        if (!uid) throw new Error('Vous devez être connecté pour réserver');
        const promoRef = doc(db, 'flashPromotions', promoId);
        const resRef = doc(db, 'reservations', `${promoId}_${uid}`);

        try {
            await runTransaction(db, async (t) => {
                const [promoSnap, resSnap] = await Promise.all([t.get(promoRef), t.get(resRef)]);
                // Réservation refusée ou annulée : le client peut réserver à nouveau.
                if (resSnap.exists() && resSnap.data().status !== 'cancelled') {
                    throw new Error('Vous avez déjà réservé cette offre flash');
                }
                const remaining = promoSnap.exists() ? promoSnap.data().quantityRemaining : 0;
                if (!promoSnap.exists() || promoSnap.data().active === false || !(remaining > 0)) {
                    throw new Error('Cette offre flash n\'est plus disponible');
                }
                // Heure de fin passée (heure de Paris) : refusée ici comme par les règles.
                if (getFlashPromoStatus(promoSnap.data(), new Date()) === 'ended') {
                    throw new Error(FLASH_PROMO_ENDED);
                }
                t.update(promoRef, { quantityRemaining: remaining - 1 });
                t.set(resRef, data);
            });
        } catch (error: any) {
            // Refus des règles : l'offre s'est terminée entre-temps (heure du serveur).
            if (error?.code === 'permission-denied') throw new Error(FLASH_PROMO_ENDED);
            throw error;
        }
        return resRef.id;
    }

    async getUserReservations(userId: string): Promise<Reservation[]> {
        try {
            const q = query(
                collection(db, 'reservations'),
                where('userId', '==', userId)
            );
            const snapshot = await getDocs(q);
            const res = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as any));
            return res.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        } catch (error) {
            console.error('Error fetching reservations:', error);
            return [];
        }
    }

    async getRestaurantReservations(restaurantId: number | string): Promise<Reservation[]> {
        try {
            const q = query(
                collection(db, 'reservations'),
                where('restaurantId', '==', String(restaurantId))
            );
            const snapshot = await getDocs(q);
            const res = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as any));
            return res.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        } catch (error) {
            console.error('Error fetching restaurant reservations:', error);
            return [];
        }
    }

    /**
     * Nombre de demandes de réservation à confirmer (pastille de la navigation) : requête
     * filtrée sur `status == 'pending'` (égalités seules, pas d'index composite) au lieu
     * d'écouter toutes les réservations du restaurant.
     */
    subscribeToPendingReservationCount(restaurantId: number | string, callback: (count: number) => void) {
        const q = query(
            collection(db, 'reservations'),
            where('restaurantId', '==', String(restaurantId)),
            where('status', '==', 'pending'),
        );
        return onSnapshot(q, (snapshot) => callback(snapshot.size), () => callback(0));
    }

    subscribeToRestaurantReservations(restaurantId: number | string, callback: (reservations: Reservation[]) => void) {
        const q = query(
            collection(db, 'reservations'),
            where('restaurantId', '==', String(restaurantId))
        );
        return onSnapshot(q, (snapshot) => {
            const res = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
                createdAt: toDate((d.data() as any).createdAt),
            } as any));
            callback(res.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        });
    }

    async updateReservationStatus(id: string, status: 'confirmed' | 'cancelled' | 'completed'): Promise<void> {
        const resRef = doc(db, 'reservations', id);
        await runTransaction(db, async (t) => {
            const resSnap = await t.get(resRef);
            const res = resSnap.data();
            // Une réservation terminée a pu créditer des points : elle ne s'annule plus (elle
            // rendrait son stock, et le client pourrait réécrire son document).
            if (status === 'cancelled' && res?.status === 'completed') {
                throw new Error('Cette réservation est déjà terminée');
            }
            // Refus ou annulation d'une réservation d'offre flash : toute la quantité revient dans
            // le stock. Le document reste (notification, historique) ; le client peut réserver à
            // nouveau en le réécrivant (règles Firestore, reservations/{resId}).
            if (res?.flashPromoId && status === 'cancelled' && res.status !== 'cancelled') {
                const promoRef = doc(db, 'flashPromotions', res.flashPromoId);
                const promoSnap = await t.get(promoRef);
                if (promoSnap.exists()) {
                    t.update(promoRef, {
                        quantityRemaining: restoredFlashStock(promoSnap.data(), flashOfferQuantity(res)),
                    });
                }
            }
            t.update(resRef, { status });
        });
        this.notifyListeners();
    }

    /**
     * Marque une réservation table comme `completed`. La CF Cloud Functions adapte
     * son comportement selon la résa :
     *   - Résa avec `userId` (réservée via l'app) → crédit fidélité auto (visit +
     *     membership + paliers + tier + boost), avec anti-doublon si déjà créditée.
     *   - Résa sans `userId` (walk-in) → close-out simple sans crédit.
     *
     * Voir `functions/src/reservations/completeTableReservation.ts`.
     */
    async completeTableReservationClassic(input: {
        reservationId: string;
        amountCents?: number;
        dishes?: Array<{ menuItemId?: string; name: string; quantity: number; unitPrice: number }>;
    }): Promise<{
        success: boolean;
        reservationId: string;
        creditApplied: boolean;
        pointsEarned: number;
        tierChanged: boolean;
        newTierName?: string;
        newTierId?: string;
        rewardsCreatedCount: number;
        alreadyCredited: boolean;
    }> {
        const fn = httpsCallable<typeof input, {
            success: boolean;
            reservationId: string;
            creditApplied: boolean;
            pointsEarned: number;
            tierChanged: boolean;
            newTierName?: string;
            newTierId?: string;
            rewardsCreatedCount: number;
            alreadyCredited: boolean;
        }>(this._fns, 'completeTableReservationClassic');
        const result = await fn(input);
        this.notifyListeners();
        return result.data;
    }

    /**
     * Returns the customer's chronological event history for a restaurant: reservations
     * (table + flash) and visits (NFC scans, reservation completions). Sorted desc, capped to 50.
     */
    async getCustomerHistory(userId: string, restaurantId: number | string): Promise<Array<{
        kind: 'reservation' | 'visit';
        id: string;
        date: Date;
        data: any;
    }>> {
        const restIdStr = String(restaurantId);
        try {
            const [resSnap, visitSnap] = await Promise.all([
                getDocs(query(
                    collection(db, 'reservations'),
                    where('userId', '==', userId),
                    where('restaurantId', '==', restIdStr),
                )),
                getDocs(query(
                    collection(db, 'visits'),
                    where('userId', '==', userId),
                    where('restaurantId', '==', restIdStr),
                )),
            ]);

            const events: Array<{ kind: 'reservation' | 'visit'; id: string; date: Date; data: any }> = [];
            const now = new Date();

            for (const d of resSnap.docs) {
                const data = d.data();
                const dateStr = data.date;
                const time = data.time || '00:00';
                const paddedTime = /^\d{2}:\d{2}$/.test(time) ? `${time}:00` : time;
                const date = new Date(`${dateStr}T${paddedTime}`);
                if (isNaN(date.getTime())) continue;
                // Skip future confirmed reservations — history is for past activity only,
                // otherwise an upcoming booking would float at the top of "past visits".
                if (data.status === 'confirmed' && date > now) continue;
                events.push({ kind: 'reservation', id: d.id, date, data });
            }

            for (const d of visitSnap.docs) {
                const data = d.data();
                const date = (data.createdAt?.toDate?.() ?? (data.createdAt ? new Date(data.createdAt) : null));
                if (date && !isNaN(date.getTime())) {
                    events.push({ kind: 'visit', id: d.id, date, data });
                }
            }

            events.sort((a, b) => b.date.getTime() - a.date.getTime());
            return events.slice(0, 50);
        } catch (error) {
            console.error('Error fetching customer history:', error);
            return [];
        }
    }

    // ============================================
    // ANALYTICS
    // ============================================

    /**
     * Données brutes pour l'écran Analyses (calcul pur dans
     * `src/shared/analytics/compute.ts`). Lecture seule, 5 requêtes filtrées
     * par restaurant. `rewardRedemptions` et `customerSegments` peuvent être
     * refusés par les règles sur une ancienne base : on continue sans eux.
     */
    async getAnalyticsRaw(restaurantId: number | string): Promise<AnalyticsRaw> {
        const restIdStr = String(restaurantId);
        const asDate = (raw: any): Date | null => {
            if (!raw) return null;
            if (raw?.toDate) return raw.toDate();
            if (typeof raw === 'string' || typeof raw === 'number') {
                const d = new Date(raw);
                return Number.isNaN(d.getTime()) ? null : d;
            }
            return raw instanceof Date ? raw : null;
        };
        const safe = async <T,>(fn: () => Promise<T>, fallback: T, what: string): Promise<T> => {
            try { return await fn(); } catch (e) { console.warn(`Analyses : ${what} non lisibles`, e); return fallback; }
        };
        const byRestaurant = (col: string) => getDocs(query(collection(db, col), where('restaurantId', '==', restIdStr)));

        // Lecture bornée : l'écran montre 6 mois (+ 1 mois d'historique pour la rétention, les
        // écarts entre visites et la fidélisation). Visites, ventes de caisse et récompenses sont
        // filtrées par date côté serveur (index composites restaurantId + date dans
        // firestore.indexes.json) : le coût ne grandit plus avec l'ancienneté du restaurant.
        // Adhésions et segments restent complets (un document par membre, nécessaire aux totaux).
        const now = new Date();
        const since = Timestamp.fromDate(new Date(now.getFullYear(), now.getMonth() - 6, 1));
        const [visitsSnap, ordersSnap, memSnap, redSnap, segSnap, program] = await Promise.all([
            getDocs(query(collection(db, 'visits'), where('restaurantId', '==', restIdStr), where('createdAt', '>=', since))),
            getDocs(query(collection(db, 'posOrders'), where('restaurantId', '==', restIdStr), where('paidAt', '>=', since))),
            byRestaurant('memberships'),
            safe(() => getDocs(query(collection(db, 'rewardRedemptions'), where('restaurantId', '==', restIdStr),
                where('status', '==', 'confirmed'), where('confirmedAt', '>=', since))), null, 'récompenses'),
            safe(() => byRestaurant('customerSegments'), null, 'segments'),
            safe(() => this.getLoyaltyProgram(restIdStr), null, 'programme'),
        ]);

        return {
            visits: visitsSnap.docs.map((d) => {
                const v = d.data() as any;
                return {
                    userId: v.userId ?? null,
                    amount: visitAmountCents(v),
                    pointsEarned: v.pointsEarned ?? null,
                    stampsEarned: v.stampsEarned ?? null,
                    createdAt: asDate(v.createdAt) ?? asDate(v.date),
                    validationMethod: v.validationMethod ?? null,
                };
            }),
            orders: ordersSnap.docs.map((d) => {
                const o = d.data() as any;
                return { customerId: o.customerId ?? o.customer?.id ?? null, total: o.total ?? null, status: o.status ?? null, paidAt: asDate(o.paidAt), createdAt: asDate(o.createdAt) };
            }),
            memberships: memSnap.docs.map((d) => {
                const m = d.data() as any;
                return { userId: String(m.userId), points: m.points ?? null, stamps: m.stamps ?? null, tier: m.tier ?? null, grade: m.grade ?? null, joinedDate: asDate(m.joinedDate) ?? asDate(m.createdAt), lastVisitAt: asDate(m.lastVisitAt) ?? asDate(m.lastVisit) };
            }),
            redemptions: (redSnap?.docs ?? []).map((d) => {
                const r = d.data() as any;
                return { userId: r.userId ?? null, status: r.status ?? null, cost: r.cost ?? null, confirmedAt: asDate(r.confirmedAt) ?? asDate(r.respondedAt) ?? asDate(r.requestedAt) };
            }),
            segments: (segSnap?.docs ?? []).map((d) => {
                const sg = d.data() as any;
                return { userId: String(sg.userId), segment: (SEGMENT_KEYS as string[]).includes(String(sg.segment)) ? sg.segment : null };
            }),
            programType: typeof (program as any)?.type === 'string' ? (program as any).type : null,
        };
    }

    async getAnalytics(restaurantId: number | string) {
        try {
            const restIdStr = String(restaurantId);

            // Fetch visits (from loyalty check-ins)
            const visitsQ = query(collection(db, 'visits'), where('restaurantId', '==', restIdStr));
            const visitsSnap = await getDocs(visitsQ);
            const visits = visitsSnap.docs.map((d) => d.data());

            // Fetch paid POS orders (the real revenue source)
            const posQ = query(
                collection(db, 'posOrders'),
                where('restaurantId', '==', restIdStr)
            );
            const posSnap = await getDocs(posQ);
            const posOrders = posSnap.docs.map(d => ({ ...d.data() } as any));
            const paidOrders = posOrders.filter(o => o.status === 'paid');

            // Memberships count
            const memQ = query(collection(db, 'memberships'), where('restaurantId', '==', restIdStr));
            const memSnap = await getDocs(memQ);
            const allMemberships = memSnap.docs.map(d => d.data());

            // New members this week
            const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
            const newMembersCount = allMemberships.filter(m => {
                const joined = m.joinedDate?.toDate?.() || (m.joinedDate ? new Date(m.joinedDate) : null);
                return joined && joined > oneWeekAgo;
            }).length;

            // Total revenue = visits + POS paid orders
            // Visites en centimes → euros. Les visites de caisse « pos » doublent leur commande payée : comptées une fois.
            const visitRevenue = visits
                .filter(v => v.validationMethod !== 'pos' && v.source !== 'pos')
                .reduce((sum, v) => sum + (visitAmountCents(v) ?? 0) / 100, 0);
            const posRevenue = paidOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
            const totalRevenue = visitRevenue + posRevenue;

            const totalVisits = visits.length + paidOrders.length;
            const averageTicket = totalVisits > 0 ? totalRevenue / totalVisits : 0;

            // Loyalty rate (members who visited > 1 time)
            const memberIds = new Set(allMemberships.map(m => m.userId));
            const memberVisits = [...visits, ...paidOrders].filter(v => v.userId && memberIds.has(v.userId));
            const uniqueReturnMembers = new Set(memberVisits.filter((v, _, arr) =>
                arr.filter(x => x.userId === v.userId).length > 1
            ).map(v => v.userId));
            const loyaltyRate = memberIds.size > 0
                ? Math.round((uniqueReturnMembers.size / memberIds.size) * 100)
                : 0;

            // Monthly revenue for charts (last 6 months using posOrders)
            const now = new Date();
            const monthlyRevenue: { month: string; revenue: number; visits: number }[] = [];
            for (let i = 5; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                const label = d.toLocaleDateString('fr-FR', { month: 'short' });
                const monthOrders = paidOrders.filter(o => {
                    let od: Date;
                    if (o.paidAt?.toDate) od = o.paidAt.toDate();
                    else if (typeof o.paidAt === 'string') od = new Date(o.paidAt);
                    else if (o.createdAt?.toDate) od = o.createdAt.toDate();
                    else if (typeof o.createdAt === 'string') od = new Date(o.createdAt);
                    else return false;
                    return `${od.getFullYear()}-${String(od.getMonth() + 1).padStart(2, '0')}` === monthKey;
                });
                monthlyRevenue.push({
                    month: label,
                    revenue: Math.round(monthOrders.reduce((s, o) => s + (Number(o.total) || 0), 0)),
                    visits: monthOrders.length
                });
            }

            // Segment distribution from memberships grades
            const segmentCounts: Record<string, number> = { Premium: 0, Fidèle: 0, Habitué: 0, Nouveau: 0, Inactif: 0, VIP: 0, Occasionnel: 0 };
            for (const m of allMemberships) {
                const g = m.grade || 'Nouveau';
                segmentCounts[g] = (segmentCounts[g] || 0) + 1;
            }

            // Visit frequency from posOrders
            const visitsByUser: Record<string, number> = {};
            for (const o of paidOrders) {
                if (o.customerId) visitsByUser[o.customerId] = (visitsByUser[o.customerId] || 0) + 1;
            }
            const freq1 = Object.values(visitsByUser).filter(v => v === 1).length;
            const freq2_3 = Object.values(visitsByUser).filter(v => v >= 2 && v <= 3).length;
            const freq4plus = Object.values(visitsByUser).filter(v => v >= 4).length;

            // ── Retention by month (last 6 months) ──
            const retentionByMonth: { month: string; rate: number }[] = [];
            for (let i = 5; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
                const label = d.toLocaleDateString('fr-FR', { month: 'short' });
                const monthVisitors = new Set(
                    [...visits, ...paidOrders].filter(v => {
                        let vd: Date;
                        if (v.createdAt?.toDate) vd = v.createdAt.toDate();
                        else if (typeof v.createdAt === 'string') vd = new Date(v.createdAt);
                        else return false;
                        return vd >= d && vd < next && v.userId && memberIds.has(v.userId);
                    }).map(v => v.userId)
                );
                retentionByMonth.push({
                    month: label,
                    rate: memberIds.size > 0 ? Math.round((monthVisitors.size / memberIds.size) * 100) : 0
                });
            }

            // ── Points earned/used by day of week (Mon–Sun) ──
            const rewardsByDay = [1, 2, 3, 4, 5, 6, 0].map((dayIdx, i) => {
                const dayName = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'][i];
                const dayVisits = visits.filter(v => {
                    let vd: Date;
                    if (v.createdAt?.toDate) vd = v.createdAt.toDate();
                    else if (typeof v.createdAt === 'string') vd = new Date(v.createdAt);
                    else return false;
                    return vd.getDay() === dayIdx;
                });
                const gagne = dayVisits.reduce((s, v) => s + (Number(v.pointsEarned) || 50), 0);
                return { name: dayName, gagne, utilise: Math.round(gagne * 0.3) };
            });

            // ── Demand by hour (lunch vs dinner) ──
            const allActivity = [...visits, ...paidOrders];
            const lunchHours = [11, 12, 13, 14];
            const dinnerHours = [18, 19, 20, 21, 22];
            const demandByHour = [...lunchHours, ...dinnerHours].map(h => {
                const cnt = allActivity.filter(v => {
                    let vd: Date;
                    if (v.createdAt?.toDate) vd = v.createdAt.toDate();
                    else if (typeof v.createdAt === 'string') vd = new Date(v.createdAt);
                    else return false;
                    return vd.getHours() === h;
                }).length;
                return {
                    hour: `${h}h`,
                    Déjeuner: lunchHours.includes(h) ? cnt : 0,
                    Dîner: dinnerHours.includes(h) ? cnt : 0,
                };
            });

            return {
                totalRevenue,
                posRevenue,
                visitRevenue,
                totalVisits,
                averageTicket,
                newMembersCount,
                loyaltyRate,
                monthlyRevenue,
                segmentCounts,
                retentionByMonth,
                rewardsByDay,
                demandByHour,
                frequencyData: [
                    { name: '1 visite', value: freq1 || 0 },
                    { name: '2-3 visites', value: freq2_3 || 0 },
                    { name: '4+ visites', value: freq4plus || 0 },
                ],
                dataSegments: [
                    { name: 'VIP/Premium', value: (segmentCounts['VIP'] || 0) + (segmentCounts['Premium'] || 0) },
                    { name: 'Habitués', value: (segmentCounts['Fidèle'] || 0) + (segmentCounts['Habitué'] || 0) },
                    { name: 'Occasionnels', value: (segmentCounts['Occasionnel'] || 0) + (segmentCounts['Nouveau'] || 0) },
                    { name: 'À risque', value: segmentCounts['Inactif'] || 0 },
                ]
            };
        } catch (error) {
            console.error('Error fetching analytics:', error);
            return {
                totalRevenue: 0, posRevenue: 0, visitRevenue: 0,
                totalVisits: 0, averageTicket: 0, newMembersCount: 0,
                loyaltyRate: 0, monthlyRevenue: [], segmentCounts: {},
                retentionByMonth: [], rewardsByDay: [], demandByHour: [],
                frequencyData: [], dataSegments: []
            };
        }
    }

    // ============================================
    // POS
    // ============================================

    /**
     * Commande de caisse lue depuis Firestore. Le client lié n'est stocké que par son
     * identifiant (+ nom, photo) : on reconstitue `customer`, sinon une commande rouverte
     * perdait son client au prochain enregistrement (customerId remis à null) et
     * l'encaissement ne créditait plus personne.
     */
    private _posOrderFromDoc(d: { id: string; data: () => any }): any {
        const data = d.data();
        const customer = typeof data.customerId === 'string' && data.customerId
            ? { id: data.customerId, name: data.customerName || 'Client', ...(safeStorageImageUrl(data.customerAvatarUrl) ? { avatarUrl: data.customerAvatarUrl } : {}) }
            : undefined;
        return { id: d.id, ...data, ...(customer ? { customer } : {}), createdAt: toDate(data.createdAt) };
    }

    async getPOSOrders(restaurantId: number | string): Promise<POSOrder[]> {
        try {
            const q = query(
                collection(db, 'posOrders'),
                where('restaurantId', '==', String(restaurantId))
                // Removed orderBy('createdAt', 'desc') to avoid composite index requirement
            );
            const snapshot = await getDocs(q);
            const orders = snapshot.docs.map((d) => this._posOrderFromDoc(d));

            // Sort locally (descending)
            return orders.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return dateB - dateA;
            });
        } catch (error) {
            console.error('Error fetching POS orders:', error);
            return [];
        }
    }

    // Listener temps réel pour les commandes POS
    subscribeToPOSOrders(restaurantId: number | string, callback: (orders: POSOrder[]) => void) {
        const q = query(
            collection(db, 'posOrders'),
            where('restaurantId', '==', String(restaurantId))
            // Removed orderBy('createdAt', 'desc') to avoid composite index requirement
        );
        return onSnapshot(q, (snapshot) => {
            const orders = snapshot.docs.map((d) => this._posOrderFromDoc(d));

            // Sort locally (descending)
            const sortedOrders = orders.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return dateB - dateA;
            });

            callback(sortedOrders);
        });
    }

    async savePOSOrder(order: POSOrder): Promise<void> {
        try {
            // Identifiant aléatoire Firestore (plus de « #CMD-1234 » devinable et réutilisable) ;
            // le numéro lisible est un champ d'affichage.
            const orderId = order.id && !order.id.startsWith('new-') ? order.id : this.newPOSOrderId();

            await setDoc(doc(db, 'posOrders', orderId), {
                ...(order.number ? { number: order.number } : {}),
                restaurantId: String((order as any).restaurantId || '1'),
                items: order.items,
                status: order.status || 'pending',
                total: order.total,
                type: order.type,
                tableNumber: order.tableNumber || null,
                customerId: order.customer?.id || null,
                customerName: order.customer?.name || null,
                customerAvatarUrl: safeStorageImageUrl(order.customer?.avatarUrl) ?? null,
                updatedAt: serverTimestamp(),
                // Date de création enregistrée (elle ne l'était pas quand la commande en avait
                // déjà une côté navigateur : heure « Invalid Date » à la réouverture).
                createdAt: order.createdAt && !Number.isNaN(new Date(order.createdAt).getTime())
                    ? Timestamp.fromDate(new Date(order.createdAt))
                    : serverTimestamp(),
            }, { merge: true });

            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de sauvegarde commande');
        }
    }

    /**
     * Encaisse une commande de la caisse : Cloud Function payPosOrder. La commande est
     * marquée payée et le membre lié crédité dans la même transaction (plats et prix de la
     * carte, même règle du jour que le tap NFC, visite « pos » en centimes, journalisé).
     * Plus aucune écriture de visite ni d'adhésion depuis le navigateur (règles Firestore).
     * Erreur : message en français (commande déjà encaissée, montant refusé…).
     */
    async payOrder(id: string, method: string): Promise<PosPaymentResult> {
        try {
            const fn = httpsCallable<{ orderId: string; paymentMethod: string }, PosPaymentResult>(this._fns, 'payPosOrder');
            const { data } = await fn({ orderId: id, paymentMethod: method });
            this.notifyListeners();
            return data;
        } catch (error: any) {
            const reason = callableErrorReason(error);
            // Déjà encaissée (double clic, autre caisse, réponse perdue puis nouvel essai) : c'est payé.
            if (reason === 'already_paid') {
                this.notifyListeners();
                return { success: true, paid: true, credited: false, reason: 'already_paid' };
            }
            // `reason` permet à la caisse de proposer « Voir l'historique » si la réponse est perdue.
            throw Object.assign(new Error(reason === 'no_response'
                ? 'Réponse non reçue : la commande est peut-être déjà payée. Vérifiez l\'historique avant de réencaisser.'
                : checkinMessage(reason)), { reason });
        }
    }

    /** Identifiant d'une nouvelle commande de caisse (aléatoire, non devinable). */
    newPOSOrderId(): string {
        return doc(collection(db, 'posOrders')).id;
    }

    /**
     * Returns lightweight member list for POS customer search.
     */
    async getRestaurantMembersForPOS(restaurantId: number | string): Promise<any[]> {
        try {
            const restIdStr = String(restaurantId);
            const memQ = query(
                collection(db, 'memberships'),
                where('restaurantId', '==', restIdStr)
            );
            const memSnap = await getDocs(memQ);
            if (memSnap.empty) return [];

            const members: any[] = [];
            for (const d of memSnap.docs) {
                const mem = d.data() as any;
                let name = 'Client';
                // Pas d'avatar généré chez un tiers (l'UID partait vers i.pravatar.cc) ni de photo
                // externe : uniquement une image de notre Storage, sinon initiales.
                let avatarUrl: string | undefined;
                try {
                    const uSnap = await getDoc(doc(db, 'users', mem.userId));
                    if (uSnap.exists()) {
                        const u = uSnap.data() as any;
                        name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Client';
                        avatarUrl = safeStorageImageUrl(u.avatarUrl) ?? undefined;
                    }
                } catch (e) { }
                members.push({
                    id: mem.userId,
                    membershipId: d.id,
                    name,
                    avatarUrl,
                    status: mem.grade || 'Nouveau',
                    loyaltyScore: mem.points || 0,
                    stamps: mem.stamps || 0,
                    points: mem.points || 0,
                    tier: mem.tier || 'Bronze',
                });
            }
            return members;
        } catch (error) {
            console.error('Error fetching POS members:', error);
            return [];
        }
    }

    async deletePOSOrder(id: string): Promise<void> {
        try {
            await deleteDoc(doc(db, 'posOrders', id));
            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de suppression');
        }
    }

    // ============================================
    // ADMIN
    // ============================================

    async deleteRestaurant(id: number | string): Promise<void> {
        const idStr = String(id);
        console.log('[Admin] Suppression restaurant:', idStr);
        try {
            await deleteDoc(doc(db, 'restaurants', idStr));
            this.notifyListeners();
            console.log('[Admin] Restaurant supprimé avec succès');
        } catch (error: any) {
            console.error('[Admin] Erreur suppression:', error.code, error.message);
            if (error.code === 'permission-denied') {
                throw new Error('Permission refusée. Vérifiez les règles Firestore ou reconnectez-vous en tant qu\'Admin.');
            }
            throw new Error(error.message || 'Erreur de suppression');
        }
    }

    async toggleRestaurantStatus(id: number | string): Promise<void> {
        try {
            const restaurant = await this.getRestaurant(id);
            if (restaurant) {
                await updateDoc(doc(db, 'restaurants', String(id)), {
                    status: restaurant.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                    updatedAt: serverTimestamp(),
                });
            }
            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de mise à jour');
        }
    }

    async toggleUserStatus(id: string): Promise<void> {
        try {
            const userDoc = await getDoc(doc(db, 'users', id));
            if (userDoc.exists()) {
                const current = userDoc.data();
                await updateDoc(doc(db, 'users', id), {
                    status: current.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
                    updatedAt: serverTimestamp(),
                });
            }
            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de mise à jour');
        }
    }

    async updateRestaurant(id: string, data: Partial<{ name: string; cuisine: string; offer: string; budget: number; ambiance: string; address: string }>): Promise<void> {
        try {
            await updateDoc(doc(db, 'restaurants', id), {
                ...data,
                updatedAt: serverTimestamp(),
            });
            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de mise à jour du restaurant');
        }
    }

    async updateUser(id: string, data: Partial<{ firstName: string; lastName: string; email: string; role: string }>): Promise<void> {
        try {
            await updateDoc(doc(db, 'users', id), {
                ...data,
                updatedAt: serverTimestamp(),
            });
            this.notifyListeners();
        } catch (error: any) {
            throw new Error(error.message || 'Erreur de mise à jour de l\'utilisateur');
        }
    }

    async sendResetEmailToUser(email: string): Promise<void> {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error: any) {
            throw new Error(error.message || 'Erreur d\'envoi d\'email');
        }
    }

    /**
     * Supprime un utilisateur :
     * - Appelle le backend (Firebase Admin SDK) pour supprimer le compte Auth
     * - Supprime le profil Firestore (quelle que soit la réponse du backend)
     */
    async deleteUser(uid: string): Promise<void> {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        let backendError: string | null = null;

        // 1. Appel backend pour supprimer Firebase Auth
        try {
            const res = await fetch(`${API_URL}/admin/users/${uid}`, { method: 'DELETE' });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                backendError = body.message || `Backend error ${res.status}`;
                console.warn('[Admin] Backend delete warning:', backendError);
            }
        } catch (e: any) {
            backendError = e.message;
            console.warn('[Admin] Backend non disponible, suppression Firestore seulement:', e.message);
        }

        // 2. Supprimer le profil Firestore (toujours, même si le backend a échoué)
        try {
            await deleteDoc(doc(db, 'users', uid));
            this.notifyListeners();
        } catch (error: any) {
            throw new Error(`Erreur suppression Firestore: ${error.message}`);
        }

        // 3. Supprimer sa demande d'accès restaurateur éventuelle (données personnelles, SIRET)
        try {
            await deleteDoc(this._applicationRef(uid));
        } catch (error: any) {
            console.error('[Admin] Suppression de la demande restaurateur:', error);
            throw new Error("Profil supprimé, mais sa demande d'accès restaurateur n'a pas pu l'être : supprimez-la depuis « Demandes restaurateurs ».");
        }

        if (backendError) {
            console.warn('[Admin] Profil Firestore supprimé mais le compte Firebase Auth nécessite une configuration du service account backend.');
        }
    }

    /**
     * Synchronise les utilisateurs (Nettoyage des orphelins Firestore sans Auth correspondant et ajout des manquants)
     */
    async syncUsers(): Promise<{ deletedCount: number; deletedIds: string[]; addedCount: number; addedIds: string[]; totalAuth: number; totalFirestore: number }> {
        let API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        // Si l'application est ouverte depuis un mobile/réseau (ex: 192.168.1.x), il faut remplacer localhost
        if (API_URL.includes('localhost') && window.location.hostname !== 'localhost') {
            API_URL = API_URL.replace('localhost', window.location.hostname);
        }

        console.log('[Admin] Calling syncUsers endpoint:', `${API_URL}/admin/sync-users`);
        try {
            const res = await fetch(`${API_URL}/admin/sync-users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                console.error('[Admin] Erreur backend syncUsers:', res.status, body);
                throw new Error(`${body.message || 'Erreur inconnue'} (HTTP ${res.status}) sur ${API_URL}`);
            }
            const result = await res.json();
            // Si des suppressions ont eu lieu, le snapshot listener devrait déjà avoir mis à jour le cache
            // Mais on force une notification au cas où
            if (result.deletedCount > 0 || result.addedCount > 0) {
                this.notifyListeners();
            }
            return result;
        } catch (error: any) {
            console.error('[Admin] Erreur de synchronisation:', error);
            throw new Error(error.message || 'Erreur de synchronisation');
        }
    }


    /**
     * Document d'un nouveau restaurant, valeurs par défaut comprises : commun à
     * la création par l'admin (createRestaurateur) et à la validation d'une
     * demande d'accès (approveRestaurateurApplication). Pas de puce ici : la première
     * est créée côté serveur depuis l'assistant de démarrage (OnboardingWizard) ou
     * l'écran « Puces NFC » (generateNfcBatch). Coordonnées : celles de l'adresse
     * (_geocodeAddress) si le géocodage a réussi, sinon un point au hasard dans Paris.
     */
    private _newRestaurantData(data: {
        restaurantName: string;
        category?: string;
        offer?: string;
        budget?: number;
        ambiance?: string;
        address?: string;
    }, coords: { lat: number; lng: number } | null = null) {
        return {
            name: data.restaurantName,
            cuisine: data.category || 'Française',
            offer: data.offer || 'Bienvenue',
            budget: data.budget || 2,
            ambiance: data.ambiance || 'Cozy',
            popularity: 'Nouveau',
            distance: Math.floor(Math.random() * 1000),
            status: 'ACTIVE',
            aggregateRating: 0,
            visitCount: 0,
            rewardScore: 0,
            lat: coords?.lat ?? 48.8566 + (Math.random() * 0.02 - 0.01),
            lng: coords?.lng ?? 2.3522 + (Math.random() * 0.02 - 0.01),
            menu: [],
            ...(data.address ? { address: data.address } : {}),
            createdAt: serverTimestamp(),
        };
    }

    /**
     * Adresse → coordonnées via Nominatim (OpenStreetMap), comme l'éditeur
     * « Mon établissement » (RestaurantProfileEditor). null si l'adresse est vide,
     * introuvable ou si le service ne répond pas dans les 8 s.
     */
    private async _geocodeAddress(address?: string): Promise<{ lat: number; lng: number } | null> {
        if (!address?.trim()) return null;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8_000);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
                { headers: { 'Accept-Language': 'fr' }, signal: controller.signal },
            );
            const json = await res.json();
            const lat = parseFloat(json?.[0]?.lat);
            const lng = parseFloat(json?.[0]?.lon);
            return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
        } catch (error) {
            console.warn('[Géocodage] adresse non localisée, coordonnées par défaut', error);
            return null;
        } finally {
            clearTimeout(timer);
        }
    }

    /**
     * Crée un compte RESTAURATEUR + restaurant sans déconnecter l'admin
     * Utilise une 2ème instance Firebase pour préserver la session courante
     */
    async createRestaurateur(data: any): Promise<void> {
        // Sans mot de passe saisi par l'admin : mot de passe aléatoire et email de réinitialisation obligatoire.
        const secondaryAuth = await this._createUserSecondaryApp(data.email, data.password || this._temporaryPassword());
        const uid = secondaryAuth.uid;
        try {
            // Créer le restaurant dans Firestore
            const coords = await this._geocodeAddress(data.address);
            const restoRef = await addDoc(collection(db, 'restaurants'), this._newRestaurantData(data, coords));

            // Créer le profil Firestore
            await setDoc(doc(db, 'users', uid), {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                role: 'RESTAURATEUR',
                status: 'ACTIVE',
                restaurantId: restoRef.id,
                createdAt: serverTimestamp(),
            });

            // Envoyer l'email de réinitialisation (toujours référencer l'auth principal)
            if (data.sendResetEmail || !data.password) {
                await sendPasswordResetEmail(auth, data.email);
            }

            this.notifyListeners();
        } catch (error: any) {
            console.error('[Admin] Erreur création restaurateur:', error);
            throw new Error(error.message || 'Erreur de création');
        }
    }

    async saveFcmToken(token: string): Promise<void> {
        const user = this.currentUser;
        if (!user) return;
        await updateDoc(doc(db, 'users', user.id), { fcmToken: token });
    }

    async inviteStaff(data: {
        restaurantId: string;
        email: string;
        firstName: string;
        lastName: string;
        role: 'OWNER' | 'MANAGER' | 'STAFF';
        isActive: boolean;
    }): Promise<void> {
        await addDoc(collection(db, 'staff'), {
            ...data,
            createdAt: serverTimestamp(),
        });
    }

    /**
     * Crée un compte CLIENT simple sans déconnecter l'admin
     */
    async createClient(data: { firstName: string; lastName: string; email: string; password?: string; sendResetEmail?: boolean }): Promise<void> {
        // Sans mot de passe saisi par l'admin : mot de passe aléatoire et email de réinitialisation obligatoire.
        const secondaryAuth = await this._createUserSecondaryApp(data.email, data.password || this._temporaryPassword());
        const uid = secondaryAuth.uid;
        try {
            // Créer le profil Firestore
            await setDoc(doc(db, 'users', uid), {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                role: 'CLIENT',
                status: 'ACTIVE',
                points: 0,
                memberships: [],
                createdAt: serverTimestamp(),
            });

            if (data.sendResetEmail || !data.password) {
                await sendPasswordResetEmail(auth, data.email);
            }

            this.notifyListeners();
        } catch (error: any) {
            console.error('[Admin] Erreur création client:', error);
            throw new Error(error.message || 'Erreur de création client');
        }
    }

    /**
     * Crée un utilisateur Firebase Auth via une app secondaire
     * afin de ne PAS remplacer la session de l'admin en cours
     */
    private async _createUserSecondaryApp(email: string, password: string): Promise<{ uid: string }> {
        const secondaryAppName = `secondary-${Date.now()}`;
        const firebaseConfig = {
            apiKey: app.options.apiKey,
            authDomain: app.options.authDomain,
            projectId: app.options.projectId,
            storageBucket: app.options.storageBucket,
            messagingSenderId: app.options.messagingSenderId,
            appId: app.options.appId,
        };
        const secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
        const secondaryAuth = getAuth(secondaryApp);
        try {
            const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
            const uid = credential.user.uid;
            await signOut(secondaryAuth); // Nettoyage
            return { uid };
        } catch (error: any) {
            if (error.code === 'auth/email-already-in-use') {
                throw new Error('Un compte existe déjà avec cet email.');
            }
            throw error;
        }
    }

    // ============================================
    // DEMANDES D'ACCÈS RESTAURATEUR
    // ============================================
    // restaurateurApplications/{uid} : le demandeur s'inscrit en CLIENT et crée sa
    // demande (pending) ; un admin la valide (restaurant + rôle RESTAURATEUR, en
    // une transaction) ou la refuse avec un motif. Cf. src/shared/restaurateurApplication.ts
    // et firestore.rules (match /restaurateurApplications).

    private _applicationRef(uid: string) {
        return doc(db, 'restaurateurApplications', uid);
    }

    private _toApplication(id: string, data: Record<string, any>): RestaurateurApplication {
        return {
            id,
            firstName: data.firstName ?? '',
            lastName: data.lastName ?? '',
            email: data.email ?? '',
            phone: data.phone ?? '',
            restaurantName: data.restaurantName ?? '',
            address: data.address ?? '',
            siret: data.siret ?? '',
            status: data.status ?? 'pending',
            emailVerified: data.emailVerified === true,
            termsVersion: data.termsVersion ?? '',
            termsAcceptedAt: toDate(data.termsAcceptedAt),
            createdAt: toDate(data.createdAt),
            ...(data.restaurantId ? { restaurantId: String(data.restaurantId) } : {}),
            ...(data.decidedAt ? { decidedAt: toDate(data.decidedAt) } : {}),
            ...(data.decidedBy ? { decidedBy: data.decidedBy } : {}),
            ...(data.rejectionReason ? { rejectionReason: data.rejectionReason } : {}),
            ...(data.purgeAt ? { purgeAt: toDate(data.purgeAt) } : {}),
        };
    }

    /** Message français pour une erreur Firebase Auth de l'inscription ; `field` = champ concerné. */
    private _signupError(error: any): UserFacingError & { field?: RestaurateurSignupField } {
        const withField = (message: string, field?: RestaurateurSignupField) =>
            Object.assign(new UserFacingError(message), field ? { field } : {});
        switch (error?.code) {
            case 'auth/email-already-in-use':
                return withField('Un compte atable existe déjà avec cette adresse. Connectez-vous, ou utilisez une autre adresse pour votre compte professionnel.', 'email');
            case 'auth/invalid-email':
                return withField('Adresse e-mail invalide. Exemple : jean@monrestaurant.fr', 'email');
            case 'auth/weak-password':
            case 'auth/password-does-not-meet-requirements':
                return withField('Mot de passe trop faible : choisissez-en un plus long, avec des lettres et des chiffres.', 'password');
            case 'auth/network-request-failed':
                return withField('Connexion impossible. Vérifiez votre connexion internet puis réessayez.');
            case 'auth/too-many-requests':
                return withField('Trop de tentatives. Patientez quelques minutes avant de réessayer.');
            default:
                console.error('[Inscription restaurateur]', error);
                return withField("Votre compte n'a pas pu être créé. Réessayez dans quelques instants.");
        }
    }

    /**
     * Erreur d'une action du demandeur → message générique, jamais technique
     * (pas de « règles de sécurité ») ; le détail est journalisé.
     */
    private _applicantError(error: any, fallback: string): UserFacingError {
        if (error instanceof UserFacingError) return error;
        console.error('[Demande restaurateur]', error);
        const code: string | undefined = error?.code;
        if (code === 'unavailable' || code === 'deadline-exceeded' || code === 'auth/network-request-failed') {
            return new UserFacingError('Connexion impossible. Vérifiez votre connexion internet puis réessayez.');
        }
        return new UserFacingError(fallback);
    }

    /** Erreur d'une action admin sur les demandes → message affichable, détaillé pour l'admin. */
    private _applicationError(error: any, fallback: string): UserFacingError {
        if (error instanceof UserFacingError) return error;
        const code: string | undefined = error?.code;
        if (code === 'permission-denied') {
            return new UserFacingError("Action refusée par les règles de sécurité. Reconnectez-vous ; si le problème persiste, les règles Firestore ne sont peut-être pas à jour.");
        }
        if (code === 'unavailable' || code === 'deadline-exceeded' || code === 'auth/network-request-failed') {
            return new UserFacingError('Connexion impossible. Vérifiez votre connexion internet puis réessayez.');
        }
        console.error('[Demandes restaurateur]', error);
        return new UserFacingError(fallback);
    }

    /**
     * Résultat de l'envoi automatique de l'e-mail de vérification (inscription ou
     * dépôt de la demande) : l'écran d'attente n'affirme pas qu'un e-mail est
     * parti si l'envoi a échoué. null : inconnu (page rechargée depuis).
     */
    private _verificationEmailStatus: 'sent' | 'failed' | null = null;

    getVerificationEmailStatus(): 'sent' | 'failed' | null {
        return this._verificationEmailStatus;
    }

    private async _sendVerificationEmailQuietly(): Promise<void> {
        try {
            await this._withTimeout(this.sendApplicantVerificationEmail(), 15_000);
            this._verificationEmailStatus = 'sent';
        } catch (error) {
            console.error('[Demande restaurateur] e-mail de vérification', error);
            this._verificationEmailStatus = 'failed';
        }
    }

    /** Document de la demande à sa création, tel que l'exigent les règles. */
    private _newApplicationData(input: RestaurateurApplicationInput, email: string, emailVerified: boolean) {
        return {
            firstName: input.firstName,
            lastName: input.lastName,
            email,
            phone: input.phone,
            restaurantName: input.restaurantName,
            address: input.address,
            siret: input.siret,
            status: 'pending',
            emailVerified,
            termsVersion: RESTAURATEUR_TERMS_VERSION,
            termsAcceptedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
        };
    }

    /**
     * Inscription « Je suis restaurateur » : compte Firebase Auth, profil
     * users/{uid} en CLIENT et demande en pending, écrits ensemble (batch) ;
     * puis e-mail de vérification. Le profil reste CLIENT jusqu'à la validation
     * par un admin (les règles interdisent toute auto-promotion).
     * Tant que le serveur n'a pas confirmé l'écriture, l'interface ne bascule pas
     * (_authFlowInFlight) : en cas de refus ou de coupure, le formulaire reste
     * affiché avec un message.
     */
    async registerRestaurateurApplicant(input: RestaurateurApplicationInput & { password: string }): Promise<void> {
        this._authFlowInFlight = true;
        try {
            let credential;
            try {
                credential = await createUserWithEmailAndPassword(auth, input.email, input.password);
            } catch (error: any) {
                throw this._signupError(error);
            }
            const { uid } = credential.user;
            // E-mail tel qu'enregistré par Firebase Auth : les règles exigent qu'il
            // soit celui du jeton (request.auth.token.email).
            const email = credential.user.email ?? input.email;
            const userData: Omit<User, 'id'> = {
                email,
                firstName: input.firstName,
                lastName: input.lastName,
                role: 'CLIENT',
                status: 'ACTIVE',
                createdAt: new Date().toISOString(),
            };
            const batch = writeBatch(db);
            batch.set(doc(db, 'users', uid), userData);
            batch.set(this._applicationRef(uid), this._newApplicationData(input, email, credential.user.emailVerified));
            try {
                await this._withTimeout(batch.commit(), FirestoreService.SIGNUP_WRITE_TIMEOUT_MS);
            } catch (error: any) {
                console.error('[Inscription restaurateur] écriture du profil et de la demande', error);
                const outcome = await this._abortSignup(credential.user);
                if (outcome === 'kept') {
                    throw new UserFacingError("Votre compte a été créé mais votre demande n'a pas pu être envoyée. Connectez-vous dès que votre connexion est rétablie : vous pourrez la renvoyer depuis votre espace.");
                }
                throw new UserFacingError(error?.code === 'deadline-exceeded' || error?.code === 'unavailable'
                    ? "Votre demande n'a pas pu être envoyée : la connexion est coupée ou trop lente. Vérifiez votre connexion internet puis réessayez."
                    : "Votre demande n'a pas pu être enregistrée. Réessayez dans quelques instants ; si le problème persiste, contactez l'équipe atable.");
            }
            await this._sendVerificationEmailQuietly();

            const user: User = { id: uid, ...userData };
            this.currentUser = user;
            localStorage.setItem('user', JSON.stringify(user));
        } finally {
            this._authFlowInFlight = false;
        }
        this.notifyListeners();
    }

    /**
     * Client déjà connecté (« Demander un accès professionnel ») : demande créée
     * avec son compte existant, sans nouveau compte Auth. L'écran d'attente
     * n'apparaît qu'une fois l'écriture confirmée par le serveur
     * (subscribeToMyRestaurateurApplication ignore les écritures locales).
     */
    async submitRestaurateurApplication(input: Omit<RestaurateurApplicationInput, 'email'>): Promise<void> {
        const user = auth.currentUser;
        if (!user) throw new UserFacingError('Votre session a expiré : reconnectez-vous.');
        try {
            // Jeton à jour : les règles comparent l'e-mail et sa vérification à ceux du jeton.
            await this._withTimeout(reload(user), 10_000);
            await this._withTimeout(user.getIdToken(true), 10_000);
        } catch (error) {
            throw this._applicantError(error, "Votre demande n'a pas pu être envoyée. Réessayez dans quelques instants.");
        }
        if (!user.email) throw new UserFacingError("Votre compte n'a pas d'adresse e-mail : contactez l'équipe atable.");

        const batch = writeBatch(db);
        batch.set(this._applicationRef(user.uid), this._newApplicationData({ ...input, email: user.email }, user.email, user.emailVerified));
        // Profil recréé sans nom (compte resté sans profil, cf. login) : on le complète.
        const profile = this.currentUser;
        if (profile?.id === user.uid && !profile.firstName && !profile.lastName) {
            batch.update(doc(db, 'users', user.uid), { firstName: input.firstName, lastName: input.lastName });
        }
        try {
            await this._withTimeout(batch.commit(), FirestoreService.SIGNUP_WRITE_TIMEOUT_MS);
        } catch (error: any) {
            if (error?.code === 'deadline-exceeded') {
                // L'écriture reste en file d'attente dans le SDK : elle partira au retour du réseau.
                throw new UserFacingError("La connexion semble coupée : votre demande partira automatiquement dès son retour si vous laissez cette page ouverte. Sinon, réessayez plus tard.");
            }
            throw this._applicantError(error, "Votre demande n'a pas pu être envoyée. Réessayez dans quelques instants ; si le problème persiste, contactez l'équipe atable.");
        }
        if (user.emailVerified) this._verificationEmailStatus = null;
        else await this._sendVerificationEmailQuietly();
    }

    /** Le demandeur retire sa demande en attente (règles : uniquement si `pending`). */
    async withdrawMyRestaurateurApplication(): Promise<void> {
        const user = auth.currentUser;
        if (!user) throw new UserFacingError('Votre session a expiré : reconnectez-vous.');
        try {
            await this._withTimeout(deleteDoc(this._applicationRef(user.uid)), FirestoreService.SIGNUP_WRITE_TIMEOUT_MS);
        } catch (error) {
            throw this._applicantError(error, "Votre demande n'a pas pu être retirée. Réessayez dans quelques instants.");
        }
    }

    /** (Ré)envoie l'e-mail de vérification Firebase au compte connecté. */
    async sendApplicantVerificationEmail(): Promise<void> {
        const user = auth.currentUser;
        if (!user) throw new UserFacingError('Votre session a expiré : reconnectez-vous.');
        auth.languageCode = 'fr';
        try {
            try {
                // Le lien ramène sur l'application une fois l'adresse confirmée.
                await sendEmailVerification(user, { url: `${window.location.origin}/` });
            } catch (error: any) {
                // Domaine non autorisé dans Firebase Auth : e-mail sans lien de retour.
                if (error?.code !== 'auth/unauthorized-continue-uri' && error?.code !== 'auth/invalid-continue-uri') throw error;
                await sendEmailVerification(user);
            }
        } catch (error: any) {
            if (error?.code === 'auth/too-many-requests') {
                throw new UserFacingError("Un e-mail vient déjà de vous être envoyé. Patientez quelques minutes avant d'en demander un autre.");
            }
            throw this._applicantError(error, "L'e-mail de vérification n'a pas pu être envoyé. Réessayez dans quelques instants.");
        }
    }

    /**
     * Relit l'état de vérification de l'e-mail auprès de Firebase Auth et, s'il
     * est confirmé, le reporte sur la demande en attente (visible par l'admin).
     * Les règles n'acceptent `emailVerified: true` que si le jeton le confirme
     * pour l'adresse de la demande : le jeton est donc rafraîchi avant l'écriture.
     */
    async refreshApplicantEmailVerification(): Promise<boolean> {
        const user = auth.currentUser;
        if (!user) return false;
        try {
            await this._withTimeout(reload(user), 10_000);
            if (!user.emailVerified) return false;
            await user.getIdToken(true);
            const ref = this._applicationRef(user.uid);
            const snap = await getDoc(ref);
            const data = snap.exists() ? snap.data() : null;
            if (data && data.status === 'pending' && data.emailVerified !== true && data.email === user.email) {
                await this._withTimeout(updateDoc(ref, { emailVerified: true }), FirestoreService.SIGNUP_WRITE_TIMEOUT_MS);
            }
            return true;
        } catch (error) {
            throw this._applicantError(error, "Impossible de vérifier votre adresse pour le moment. Réessayez dans quelques instants.");
        }
    }

    /**
     * Demande de l'utilisateur `uid` en temps réel (null : aucune demande).
     * Seuls les états confirmés par le serveur sont transmis : une écriture locale
     * refusée ensuite ne fait pas basculer l'écran.
     */
    subscribeToMyRestaurateurApplication(
        uid: string,
        onChange: (application: RestaurateurApplication | null) => void,
        onError: (error: Error) => void,
    ): () => void {
        return onSnapshot(
            this._applicationRef(uid),
            { includeMetadataChanges: true },
            (snap) => {
                if (snap.metadata.hasPendingWrites) return;
                onChange(snap.exists() ? this._toApplication(snap.id, snap.data()) : null);
            },
            (error) => {
                console.error('[Demande restaurateur] lecture', error);
                onError(error);
            },
        );
    }

    /** Nombre maximum de demandes écoutées (badge : en attente ; onglet : historique). */
    static readonly MAX_PENDING_APPLICATIONS = 200;
    static readonly MAX_LISTED_APPLICATIONS = 500;

    /**
     * Admin : demandes en attente (compteur du menu et du tableau de bord), les plus
     * récentes d'abord. Filtre sans tri côté serveur : pas d'index composite à déployer.
     */
    subscribeToPendingRestaurateurApplications(
        onChange: (applications: RestaurateurApplication[]) => void,
        onError: (message: string) => void,
    ): () => void {
        return onSnapshot(
            query(
                collection(db, 'restaurateurApplications'),
                where('status', '==', 'pending'),
                limit(FirestoreService.MAX_PENDING_APPLICATIONS),
            ),
            (snap) => onChange(snap.docs
                .map((d) => this._toApplication(d.id, d.data()))
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))),
            (error) => onError(this._applicationError(error, 'Impossible de charger les demandes restaurateurs.').message),
        );
    }

    /** Admin : historique des demandes (les 500 plus récentes), chargé à l'ouverture de l'onglet. */
    subscribeToRestaurateurApplications(
        onChange: (applications: RestaurateurApplication[]) => void,
        onError: (message: string) => void,
    ): () => void {
        return onSnapshot(
            query(
                collection(db, 'restaurateurApplications'),
                orderBy('createdAt', 'desc'),
                limit(FirestoreService.MAX_LISTED_APPLICATIONS),
            ),
            (snap) => onChange(snap.docs.map((d) => this._toApplication(d.id, d.data()))),
            (error) => onError(this._applicationError(error, 'Impossible de charger les demandes restaurateurs.').message),
        );
    }

    /**
     * Admin : valide une demande en une transaction — création du restaurant
     * (mêmes valeurs par défaut que createRestaurateur, coordonnées de l'adresse),
     * profil passé en RESTAURATEUR avec son restaurant, demande approuvée. Exige
     * un e-mail vérifié et un compte CLIENT actif. Le demandeur bascule sur son
     * tableau de bord dès que son profil change (écoute temps réel).
     * Retourne l'identifiant du restaurant créé.
     */
    async approveRestaurateurApplication(applicationId: string): Promise<string> {
        const adminUid = auth.currentUser?.uid;
        if (!adminUid) throw new UserFacingError('Votre session a expiré : reconnectez-vous.');
        const applicationRef = this._applicationRef(applicationId);
        const userRef = doc(db, 'users', applicationId);
        const restaurantRef = doc(collection(db, 'restaurants'));
        // Géocodage hors transaction (réseau externe) ; l'adresse ne peut plus changer
        // une fois la demande créée (règles).
        let address: string | undefined;
        try {
            const snap = await getDoc(applicationRef);
            address = snap.exists() ? snap.data().address : undefined;
        } catch (error) {
            throw this._applicationError(error, "La demande n'a pas pu être lue. Réessayez dans quelques instants.");
        }
        const coords = await this._geocodeAddress(address);
        try {
            await runTransaction(db, async (tx) => {
                const applicationSnap = await tx.get(applicationRef);
                if (!applicationSnap.exists()) throw new UserFacingError("Cette demande n'existe plus.");
                const application = applicationSnap.data();
                if (application.status !== 'pending') throw new UserFacingError('Cette demande a déjà été traitée.');
                if (application.emailVerified !== true) {
                    throw new UserFacingError("L'adresse e-mail du demandeur n'est pas encore vérifiée : la demande ne peut pas être validée.");
                }
                const userSnap = await tx.get(userRef);
                if (!userSnap.exists()) {
                    throw new UserFacingError("Le compte du demandeur n'existe plus : la demande ne peut pas être validée.");
                }
                const profile = userSnap.data();
                if (profile.role !== 'CLIENT') {
                    throw new UserFacingError(`Ce compte a déjà le rôle ${profile.role} : la demande ne peut pas être validée.`);
                }
                if ((profile.status ?? 'ACTIVE') !== 'ACTIVE') {
                    throw new UserFacingError("Ce compte est suspendu : réactivez-le avant de valider la demande.");
                }
                tx.set(restaurantRef, this._newRestaurantData({
                    restaurantName: application.restaurantName,
                    address: application.address,
                }, coords));
                tx.update(userRef, {
                    role: 'RESTAURATEUR',
                    restaurantId: restaurantRef.id,
                    restaurantName: application.restaurantName,
                    updatedAt: serverTimestamp(),
                });
                tx.update(applicationRef, {
                    status: 'approved',
                    restaurantId: restaurantRef.id,
                    decidedAt: serverTimestamp(),
                    decidedBy: adminUid,
                });
            });
        } catch (error) {
            throw this._applicationError(error, "La demande n'a pas pu être validée. Réessayez dans quelques instants.");
        }
        this.notifyListeners();
        return restaurantRef.id;
    }

    /**
     * Admin : refuse une demande en attente ; le motif est affiché au demandeur.
     * La demande sera supprimée automatiquement à `purgeAt` (politique TTL Firestore).
     */
    async rejectRestaurateurApplication(applicationId: string, reason: string): Promise<void> {
        const adminUid = auth.currentUser?.uid;
        if (!adminUid) throw new UserFacingError('Votre session a expiré : reconnectez-vous.');
        const check = validateRejectionReason(reason);
        if (check.ok === false) throw new UserFacingError(check.error);
        const applicationRef = this._applicationRef(applicationId);
        try {
            await runTransaction(db, async (tx) => {
                const applicationSnap = await tx.get(applicationRef);
                if (!applicationSnap.exists()) throw new UserFacingError("Cette demande n'existe plus.");
                if (applicationSnap.data().status !== 'pending') throw new UserFacingError('Cette demande a déjà été traitée.');
                tx.update(applicationRef, {
                    status: 'rejected',
                    rejectionReason: check.value,
                    decidedAt: serverTimestamp(),
                    decidedBy: adminUid,
                    purgeAt: Timestamp.fromMillis(Date.now() + REJECTED_APPLICATION_RETENTION_DAYS * 86_400_000),
                });
            });
        } catch (error) {
            throw this._applicationError(error, "Le refus n'a pas pu être enregistré. Réessayez dans quelques instants.");
        }
    }

    /** Admin : supprime une demande, quel que soit son statut (RGPD, nouvelle demande après refus). */
    async deleteRestaurateurApplication(applicationId: string): Promise<void> {
        try {
            await this._withTimeout(deleteDoc(this._applicationRef(applicationId)), FirestoreService.SIGNUP_WRITE_TIMEOUT_MS);
        } catch (error) {
            throw this._applicationError(error, "La demande n'a pas pu être supprimée. Réessayez dans quelques instants.");
        }
    }

    // ============================================
    // HELPERS
    // ============================================

    activatePromo(userId: string, promoId: string) {
        console.log(`Promo ${promoId} activated for user ${userId}`);
    }
}

// Export singleton
export const firestoreService = new FirestoreService();
export default firestoreService;
